(() => {
  if (window.__groovDevelopmentRuntimeLoaded) return;
  window.__groovDevelopmentRuntimeLoaded = true;

  const CONFIG = Object.freeze({
    initialTextureY: -32,
    parallaxDistance: 420,
    parallaxIntensity: 1,
    crossfade: Object.freeze({
      start: 0,
      end: 1,
    }),
    timedTransition: Object.freeze({
      expertsCardsViewportY: 0,
    }),
    thirdTextureCrossfade: Object.freeze({
      start: 0.5,
      end: 1,
    }),
    textureOpacity: Object.freeze({
      texture1: Object.freeze({ from: 1, to: 0 }),
      texture2: Object.freeze({ from: 0, to: 1 }),
      texture3: Object.freeze({ from: 0, to: 1 }),
    }),
    statement: Object.freeze({
      characterRevealStart: 0.04,
      characterRevealEnd: 0.58,
      characterFadeOverlap: 1.65,
      // Let "We make shoes" finish before the insole starts entering.
      insoleStart: 0.48,
      insoleEnd: 1,
      insoleEnterViewportY: 0.72,
      insoleExitViewportY: -0.5,
      insoleRotationStart: 24,
      insoleRotationEnd: -20,
      insoleScaleStart: 0.78,
      insoleScaleEnd: 1.05,
      insoleFadeInStart: 0.48,
      insoleFadeInEnd: 0.6,
      insoleFadeOutStart: 0.82,
      insoleFadeOutEnd: 1,
      scrollVideo: Object.freeze({
        // Paired with the desktop section height: 600vh base + 100vh video extension.
        desktopExtraScrollViewport: 1,
        // Keep the video scrub timing independent from the physical motion.
        motionEnterEnd: 0.36,
        motionExitStart: 0.72,
        enterEnd: 0.24,
        scrubEnd: 0.8,
        fadeInEnd: 0.32,
        fadeOutStart: 0.82,
        defaultFrameRate: 60,
        settledRotation: 0,
        settledScale: 1,
      }),
      animation2: Object.freeze({
        secondInsoleStartViewportX: -0.1,
        secondInsoleEndViewportX: 0.06,
        secondInsoleOffsetViewportY: 0.62,
        secondInsoleRotationStart: -8,
        // secondInsoleRotationEnd: 18,
        // secondInsoleRotationEnd: -58,
        secondInsoleRotationEnd: 24,
        secondInsoleScaleStart: 0.82,
        secondInsoleScaleEnd: 0.98,
        mobileSecondInsoleStartViewportX: -0.08,
        mobileSecondInsoleEndViewportX: 0.04,
        mobileSecondInsoleOffsetViewportY: 0.54,
        mobileSecondInsoleRotationStart: -8,
        mobileSecondInsoleRotationEnd: 18,
        mobileSecondInsoleScale: 1,
      }),
      mobileCompact: Object.freeze({
        insoleEnterViewportY: 0.37,
        insoleExitViewportY: -0.27,
        insoleScale: 1.05,
        retainEndOpacity: true,
        releaseTailScrollViewport: 0.18,
        releaseTailLiftViewportY: -0.07,
        releaseTailRotation: -4,
      }),
      mobileCompactSynced: Object.freeze({
        characterRevealStart: 0.04,
        characterRevealEnd: 0.96,
        insoleStart: 0.78,
        insoleEnd: 0.96,
        insoleFadeInStart: 0.78,
        insoleFadeInEnd: 0.88,
      }),
      mobileCompactSyncedTail: Object.freeze({
        characterRevealStart: 0.04,
        characterRevealEnd: 0.4,
        insoleStart: 0.25,
        insoleEnd: 1,
        insoleFadeInStart: 0.25,
        insoleFadeInEnd: 0.4,
        fadeOutStart: 0.94,
        releaseTailScrollViewport: 0.16,
        releaseTailLiftViewportY: -0.06,
        releaseTailRotation: -4,
      }),
    }),
    statementToExperts: Object.freeze({
      startAfterStatementEndViewportY: 0.42,
      endAfterStatementEndViewportY: 1.1,
      expertsTranslateStartViewportY: 0,
      statementFadeStartBeforeStatementEndViewportY: 0.22,
      statementFadeEndAfterStatementEndViewportY: 0.62,
      headingStart: 0,
      headingEnd: 0.34,
      copyStart: 0.1,
      copyEnd: 0.46,
      cardStart: 0.4,
      cardStep: 0.09,
      cardDuration: 0.33,
      desktopCardStart: 0.16,
      desktopCardStep: 0.04,
      desktopCardDuration: 0.25,
      cardOrder: Object.freeze(['von', 'krista', 'sophie', 'trevor']),
    }),
  });

  const DEVELOPMENT_STEPS_STICKY_HEADER_COMPENSATION = 40;

  const ORBIT_CONFIG = Object.freeze({
    duration: 24000,
    radiusX: 0.435,
    radiusY: 0.87,
    centerY: 1,
    tangentRotation: 0.65,
    edgeFade: 0.12,
  });

  const LIQUID_GLASS_CONFIG = Object.freeze({
    maxDevicePixelRatio: 1.25,
  });

  const clamp = (value, min = 0, max = 1) => Math.min(Math.max(value, min), max);
  const mix = (from, to, progress) => from + (to - from) * progress;
  const progressBetween = (value, start, end) => clamp((value - start) / Math.max(end - start, 0.0001));
  const extendStatementVideoProgress = (progress, totalDistance, extraDistance) => {
    if (extraDistance <= 0 || totalDistance <= extraDistance) {
      return { textProgress: progress, mediaProgress: progress };
    }

    const baseDistance = totalDistance - extraDistance;
    const position = progress * totalDistance;
    const statement = CONFIG.statement;
    const centerStart = mix(statement.insoleStart, statement.insoleEnd,
      statement.scrollVideo.motionEnterEnd) * baseDistance;
    const centerEnd = mix(statement.insoleStart, statement.insoleEnd,
      statement.scrollVideo.motionExitStart) * baseDistance;
    // Stretch only the centered portion; entry stays in place and exit shifts intact.
    const mediaPosition = position <= centerStart
      ? position
      : position < centerEnd + extraDistance
        ? mix(centerStart, centerEnd, progressBetween(position, centerStart, centerEnd + extraDistance))
        : position - extraDistance;

    return {
      textProgress: clamp(position / baseDistance),
      mediaProgress: clamp(mediaPosition / baseDistance),
    };
  };
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const easeOutCubic = (value) => 1 - Math.pow(1 - value, 3);
  const easeInOutCubic = (value) => value < 0.5
    ? 4 * value * value * value
    : 1 - Math.pow(-2 * value + 2, 3) / 2;

  const getLayoutViewportWidth = () =>
    document.documentElement.clientWidth || window.innerWidth || 0;

  const shouldUseIOSAlphaVideo = (video) => {
    if (!video?.canPlayType('video/mp4; codecs="hvc1"')) {
      return false;
    }

    const userAgent = navigator.userAgent || '';
    return /iPad|iPhone|iPod/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const developmentScriptPromises = new Map();
  const developmentYotpoReviewPromises = new Map();

  const fetchDevelopmentYotpoReviews = (appKey) => {
    if (developmentYotpoReviewPromises.has(appKey)) {
      return developmentYotpoReviewPromises.get(appKey);
    }

    const endpoint = new URL(
      `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/yotpo_site_reviews/reviews.json`
    );
    endpoint.searchParams.set('per_page', '50');
    endpoint.searchParams.set('page', '1');

    const request = fetch(endpoint, {
      credentials: 'omit',
      headers: { Accept: 'application/json' },
      mode: 'cors',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Yotpo returned HTTP ${response.status}.`);
        return response.json();
      })
      .then((payload) => {
        if (payload?.status?.code !== 200 || !Array.isArray(payload?.response?.reviews)) {
          throw new Error('Yotpo returned an invalid reviews payload.');
        }

        return payload.response.reviews;
      })
      .catch((error) => {
        developmentYotpoReviewPromises.delete(appKey);
        throw error;
      });

    developmentYotpoReviewPromises.set(appKey, request);
    return request;
  };

  const loadDevelopmentScript = (source, isReady) => {
    if (isReady()) return Promise.resolve();
    if (!source) return Promise.reject(new Error('Missing external script URL.'));
    if (developmentScriptPromises.has(source)) return developmentScriptPromises.get(source);

    const promise = new Promise((resolve, reject) => {
      const existing = [...document.scripts].find((script) => script.src === source);
      const script = existing || document.createElement('script');
      const onLoad = () => {
        script.dataset.developmentLoaded = 'true';
        cleanup();
        isReady()
          ? resolve()
          : reject(new Error(`External script loaded without its expected global: ${source}`));
      };
      const onError = () => {
        cleanup();
        reject(new Error(`Could not load external script: ${source}`));
      };
      const cleanup = () => {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });

      if (existing) {
        if (script.dataset.developmentLoaded === 'true') onLoad();
        return;
      }

      script.src = source;
      script.async = true;
      script.dataset.developmentExternalScript = 'true';
      document.head.appendChild(script);
    });

    developmentScriptPromises.set(source, promise);
    return promise;
  };

  class DevelopmentScrollSmootherController {
    constructor(root) {
      this.root = root;
      this.enabled = root.dataset.developmentScrollSmootherEnabled === 'true';
      this.mobileEnabled = root.dataset.developmentScrollSmootherMobileEnabled === 'true';
      this.smooth = clamp(
        Number(root.dataset.developmentScrollSmootherSmooth || 1),
        0.2,
        2
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileMedia = window.matchMedia(
        '(max-width: 749px), (hover: none) and (pointer: coarse)'
      );
      this.destroyed = false;
      this.loadVersion = 0;
      this.stepsSidebarResizeObserver = null;
      this.stickyRefreshFrame = null;

      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onSmootherUpdate = this.onSmootherUpdate.bind(this);
    }

    init() {
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      this.mobileMedia.addEventListener?.('change', this.onBreakpointChange);

      if (!this.enabled) {
        this.setState('disabled');
        return;
      }

      if (!this.shouldEnable()) {
        this.setState('mobile-disabled');
        return;
      }

      if (this.reducedMotion.matches) {
        this.setState('reduced-motion');
        return;
      }

      this.start();
    }

    async start() {
      if (this.destroyed || this.smoother || this.loading) return;

      const version = ++this.loadVersion;
      this.loading = true;
      this.setState('loading');

      try {
        await loadDevelopmentScript(
          this.root.dataset.developmentGsapUrl,
          () => Boolean(window.gsap)
        );
        await loadDevelopmentScript(
          this.root.dataset.developmentScrollTriggerUrl,
          () => Boolean(window.ScrollTrigger)
        );
        await loadDevelopmentScript(
          this.root.dataset.developmentScrollSmootherUrl,
          () => Boolean(window.ScrollSmoother)
        );

        if (
          this.destroyed
          || version !== this.loadVersion
          || this.reducedMotion.matches
          || !this.shouldEnable()
        ) return;

        window.gsap.registerPlugin(window.ScrollTrigger, window.ScrollSmoother);
        this.mountPage();

        const existingSmoother = window.ScrollSmoother.get?.();
        if (existingSmoother && existingSmoother !== this.smoother) existingSmoother.kill();

        document.documentElement.classList.add('development-scroll-smoother-active');
        this.smoother = window.ScrollSmoother.create({
          wrapper: this.wrapper,
          content: this.content,
          smooth: this.smooth,
          effects: true,
          onUpdate: this.onSmootherUpdate,
        });
        this.syncHeader();

        this.createStickyPins();
        this.setState('active');
        this.refreshDevelopmentLayout();
      } catch (error) {
        this.killStickyPins();
        this.smoother?.kill();
        this.smoother = null;
        document.documentElement.classList.remove('development-scroll-smoother-active');
        this.unmountPage();
        this.setState('fallback');
        console.warn('Development ScrollSmoother could not initialize.', error);
      } finally {
        this.loading = false;
      }
    }

    mountPage() {
      if (this.wrapper) return;

      const main = this.root.closest('#MainContent') || this.root.closest('#main');
      const parent = main?.parentNode;
      if (!main || !parent) throw new Error('Development ScrollSmoother requires #MainContent or #main.');

      this.main = main;
      this.parent = parent;
      this.content = main.closest('[data-development-scroll-smoother-content]');
      this.wrapper = this.content?.closest('[data-development-scroll-smoother-wrapper]');
      this.usesLayoutWrapper = Boolean(this.wrapper);

      if (!this.usesLayoutWrapper) {
        // Support sections placed outside the homepage/development layout.
        this.anchor = document.createComment('development-scroll-smoother');
        this.wrapper = document.createElement('div');
        this.content = document.createElement('div');
        this.wrapper.className = 'development-scroll-smoother-wrapper';
        this.content.className = 'development-scroll-smoother-content';
        this.wrapper.dataset.developmentScrollSmootherWrapper = '';
        this.content.dataset.developmentScrollSmootherContent = '';
        parent.insertBefore(this.anchor, main);
        parent.insertBefore(this.wrapper, main);
        this.wrapper.appendChild(this.content);
        this.content.appendChild(main);
        [...parent.children]
          .filter((element) => element.classList.contains('shopify-section-group-footer-group'))
          .forEach((footerSection) => this.content.appendChild(footerSection));
      }

      this.headerSection = this.content.querySelector(':scope > .shopify-section--header');
      this.header = this.headerSection?.querySelector('store-header');
      if (!this.header) return;

      this.headerSlot = document.createElement('div');
      this.headerSlot.setAttribute('aria-hidden', 'true');
      this.headerSection.before(this.headerSlot);
      this.headerObserver = new MutationObserver(() => this.updateHeaderDock());
      this.headerObserver.observe(this.header, { attributes: true, attributeFilter: ['class'] });
      this.headerResizeObserver = new ResizeObserver(() => {
        if (this.headerDocked) this.headerSlot.style.height = `${this.headerSection.getBoundingClientRect().height}px`;
      });
      this.headerResizeObserver.observe(this.headerSection);
    }

    syncHeader(smoother = this.smoother) {
      if (!smoother || !this.header) return;

      // This value controls only the existing sticky transition. The header,
      // announcement and hero share one transform while they scroll in flow.
      const scrollY = smoother.scrollTop();
      if (this.header.developmentScrollY !== scrollY) {
        this.header.developmentScrollY = scrollY;
        this.header.dispatchEvent(new Event('development:scroll'));
      }
      this.updateHeaderDock();
    }

    moveHeader(parent, before) {
      // Preserve focus/custom-element state where supported. The header also
      // guards its event registration for browsers that reconnect moved nodes.
      if (parent.moveBefore) parent.moveBefore(this.headerSection, before);
      else parent.insertBefore(this.headerSection, before);
    }

    updateHeaderDock() {
      if (!this.header || !this.headerSlot) return;

      const shouldDock = this.header.hasAttribute('sticky') &&
        this.header.classList.contains('is-filled') &&
        (this.header._hasSwitchedToSticky || this.headerDocked);
      if (Boolean(shouldDock) === Boolean(this.headerDocked)) return;

      if (shouldDock) {
        // Preserve the header's place in the page; only the sticky mode lives
        // outside the transformed content so it can stay fixed to the viewport.
        this.headerSlot.style.height = `${this.headerSection.getBoundingClientRect().height}px`;
        this.headerDocked = true;
        this.headerSection.classList.add('development-header-docked');
        this.moveHeader(this.wrapper.parentNode, this.wrapper);
      } else {
        this.restoreHeader();
      }
    }

    restoreHeader() {
      if (!this.headerDocked) return;

      this.headerDocked = false;
      this.headerSection.classList.remove('development-header-docked');
      this.moveHeader(this.headerSlot.parentNode, this.headerSlot.nextSibling);
      this.headerSlot.style.removeProperty('height');
    }

    createStickyPins() {
      const background = this.root.querySelector('.development__background');
      const statementPanel = this.root.querySelector('[data-development-panel="statement"]');
      const statementStage = statementPanel?.querySelector('.development__statement-stage');
      const stepsScroller = this.root.querySelector('[data-development-steps-mode="scroll"]');
      const stepsSidebar = stepsScroller?.querySelector('.development-steps__sidebar');
      this.stickyTriggers = [];

      if (background) {
        this.stickyTriggers.push(window.ScrollTrigger.create({
          trigger: this.root,
          start: 'top top',
          end: 'bottom bottom',
          pin: background,
          pinSpacing: false,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        }));
      }

      if (statementPanel && statementStage) {
        this.stickyTriggers.push(window.ScrollTrigger.create({
          trigger: statementStage,
          endTrigger: statementPanel,
          start: 'top top',
          end: 'bottom bottom',
          pin: statementStage,
          pinSpacing: false,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        }));
      }

      if (stepsScroller && stepsSidebar && !this.mobileMedia.matches) {
        const getStickyOffset = () => {
          const stickyOffset = Math.max((window.innerHeight - stepsSidebar.offsetHeight) / 2, 0) +
            DEVELOPMENT_STEPS_STICKY_HEADER_COMPENSATION;
          stepsSidebar.style.setProperty('--development-steps-sticky-offset', `${stickyOffset}px`);
          return stickyOffset;
        };

        if ('ResizeObserver' in window) {
          this.stepsSidebarResizeObserver = new ResizeObserver(() => {
            if (this.stickyRefreshFrame !== null) {
              window.cancelAnimationFrame(this.stickyRefreshFrame);
            }

            this.stickyRefreshFrame = window.requestAnimationFrame(() => {
              this.stickyRefreshFrame = null;
              if (!this.destroyed && this.smoother) window.ScrollTrigger.refresh();
            });
          });
          this.stepsSidebarResizeObserver.observe(stepsSidebar);
        }

        getStickyOffset();
        // The start offset centers the sidebar with its header compensation.
        // Changing its top when the pin activates would apply the offset twice.
        this.stickyTriggers.push(window.ScrollTrigger.create({
          trigger: stepsScroller,
          start: () => `top top+=${getStickyOffset()}`,
          end: () => `bottom top+=${getStickyOffset() + stepsSidebar.offsetHeight}`,
          pin: stepsSidebar,
          pinSpacing: false,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        }));
      }
    }

    killStickyPins() {
      this.stepsSidebarResizeObserver?.disconnect();
      this.stepsSidebarResizeObserver = null;
      if (this.stickyRefreshFrame !== null) {
        window.cancelAnimationFrame(this.stickyRefreshFrame);
        this.stickyRefreshFrame = null;
      }
      this.stickyTriggers?.forEach((trigger) => trigger.kill());
      this.stickyTriggers = [];
    }

    unmountPage() {
      if (!this.wrapper || !this.parent) return;

      this.headerObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      this.restoreHeader();
      this.headerSlot?.remove();
      if (this.header) {
        delete this.header.developmentScrollY;
        this.header.dispatchEvent(new Event('development:scroll'));
      }

      if (!this.usesLayoutWrapper) {
        while (this.content?.firstChild) {
          this.parent.insertBefore(this.content.firstChild, this.wrapper);
        }
        this.wrapper.remove();
        this.anchor?.remove();
      }
      this.wrapper = null;
      this.content = null;
      this.anchor = null;
      this.parent = null;
      this.main = null;
      this.header = null;
      this.headerSection = null;
      this.headerSlot = null;
      this.headerObserver = null;
      this.headerResizeObserver = null;
    }

    onSmootherUpdate(smoother) {
      this.syncHeader(smoother);
      this.root.developmentFrameCoordinator?.request();
    }

    onMotionPreferenceChange() {
      if (!this.shouldEnable() || this.destroyed) return;

      if (this.reducedMotion.matches) {
        this.stop('reduced-motion');
      } else {
        this.start();
      }
    }

    onBreakpointChange() {
      if (!this.enabled || this.destroyed) return;

      if (!this.shouldEnable()) {
        this.stop('mobile-disabled');
      } else if (this.reducedMotion.matches) {
        this.stop('reduced-motion');
      } else {
        this.start();
      }
    }

    shouldEnable() {
      return this.enabled && (this.mobileEnabled || !this.mobileMedia.matches);
    }

    refreshDevelopmentLayout() {
      window.requestAnimationFrame(() => {
        if (this.destroyed || !this.smoother) return;
        this.root.developmentScrollController?.schedule(true);
        this.root.developmentFrameCoordinator?.request();
        window.ScrollTrigger?.refresh();
        this.root.dispatchEvent(new CustomEvent('development:viewportchange'));
      });
    }

    getScrollY() {
      const end = this.smoother?.scrollTrigger?.end;
      const progress = this.smoother?.progress;
      if (Number.isFinite(end) && Number.isFinite(progress)) return end * progress;

      return window.scrollY || document.documentElement.scrollTop;
    }

    scrollTo(target, smooth = true, position = 'center center') {
      if (!this.smoother || !target) return false;

      this.smoother.scrollTo(target, smooth, position);
      return true;
    }

    setState(state) {
      this.root.dataset.developmentScrollSmootherState = state;
    }

    stop(state = 'disabled') {
      ++this.loadVersion;
      this.killStickyPins();
      this.smoother?.kill();
      this.smoother = null;
      document.documentElement.classList.remove('development-scroll-smoother-active');
      this.unmountPage();
      this.setState(state);
      this.root.developmentScrollController?.schedule(true);
    }

    destroy() {
      this.destroyed = true;
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      this.mobileMedia.removeEventListener?.('change', this.onBreakpointChange);
      this.stop('destroyed');
    }
  }

  class DevelopmentFrameCoordinator {
    constructor() {
      this.callbacks = new Set();
      this.frame = null;
      this.destroyed = false;
      this.layoutRectCache = new WeakMap();

      this.flush = this.flush.bind(this);
    }

    add(callback) {
      this.callbacks.add(callback);
    }

    remove(callback) {
      this.callbacks.delete(callback);
    }

    request() {
      if (this.frame !== null || this.destroyed) return;
      this.frame = window.requestAnimationFrame(this.flush);
    }

    getRect(element) {
      if (!element) return null;

      const cachedRect = this.layoutRectCache.get(element);
      if (cachedRect) return cachedRect;

      const rect = element.getBoundingClientRect();
      this.layoutRectCache.set(element, rect);
      return rect;
    }

    flush(timestamp) {
      this.frame = null;
      this.layoutRectCache = new WeakMap();
      this.callbacks.forEach((callback) => callback(timestamp));
    }

    destroy() {
      this.destroyed = true;
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.callbacks.clear();
    }
  }

  class DevelopmentRevealController {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-development]');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileOnly = root.hasAttribute('data-development-reveal-mobile');
      this.mobileMedia = this.mobileOnly ? window.matchMedia('(max-width: 749px)') : null;
      this.previousReveal = root.dataset.developmentRevealAfter
        ? document.getElementById(root.dataset.developmentRevealAfter)
        : null;
      this.frame = null;
      this.usesSectionViewport = false;
      this.order = clamp(Number(root.dataset.developmentRevealOrder || 1), 1, this.mobileOnly ? 6 : 5);
      this.startLine = clamp(
        Number(root.dataset.developmentRevealStartLine || (88 - (this.order - 1) * 4)) / 100,
        0.1,
        0.95
      );
      const defaultEndLine = this.startLine - 0.26;
      const configuredEndLine = Number(root.dataset.developmentRevealEndLine);
      this.endLine = clamp(
        (Number.isFinite(configuredEndLine) ? configuredEndLine / 100 : defaultEndLine),
        0.05,
        this.startLine - 0.04
      );
      this.distance = clamp(Number(root.dataset.developmentRevealY || 34), 0, 96);

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.runFrame = this.runFrame.bind(this);
    }

    init() {
      this.render(this.reducedMotion.matches || (this.mobileOnly && !this.mobileMedia.matches) ? 1 : 0);

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.reducedMotion.addEventListener?.('change', this.onResize);
      this.mobileMedia?.addEventListener?.('change', this.onResize);
      if (this.mobileOnly) {
        this.root.addEventListener('focusin', this.onResize);
        this.root.addEventListener('focusout', this.onResize);
      }
      this.usesSectionViewport = Boolean(this.section?.developmentScrollController?.initialized);

      if (this.usesSectionViewport) {
        this.section.addEventListener('development:viewportchange', this.onResize);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      // Reveal thresholds intentionally follow the visible viewport. Keep this
      // lightweight render update separate from the sequence's stable geometry.
      window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });

      this.schedule();
    }

    onScroll() {
      this.schedule();
    }

    onResize() {
      this.schedule();
    }

    schedule() {
      if (this.frame !== null) return;
      this.frame = window.requestAnimationFrame(this.runFrame);
    }

    runFrame() {
      this.frame = null;
      this.render();
    }

    render(forcedProgress = null) {
      const enabled = !this.mobileOnly || this.mobileMedia.matches;
      const progress = forcedProgress ?? this.getProgress();
      const easedProgress = easeInOutCubic(progress);

      this.root.style.setProperty('--development-reveal-opacity', easedProgress.toFixed(4));
      this.root.style.setProperty(
        '--development-reveal-offset',
        `${mix(this.distance, 0, easedProgress).toFixed(2)}px`
      );
      this.root.classList.toggle('is-development-reveal-pending', enabled);
      this.root.classList.toggle('is-development-revealed', enabled && progress >= 1);
    }

    getRevealRange(viewportHeight) {
      const rect = this.root.getBoundingClientRect();
      // Measure the layout position so the element's own slide does not
      // feed back into its scroll progress.
      const offset = Number.parseFloat(
        this.root.style.getPropertyValue('--development-reveal-offset')
      ) || 0;
      let start = rect.top - offset - viewportHeight * this.startLine;
      const previousController = this.previousReveal?.developmentRevealController;
      if (previousController) {
        start = Math.max(start, previousController.getRevealRange(viewportHeight).end);
      }
      return {
        start,
        end: start + viewportHeight * (this.startLine - this.endLine),
      };
    }

    getProgress() {
      if (this.reducedMotion.matches || (this.mobileOnly && !this.mobileMedia.matches)) return 1;
      if (this.mobileOnly && this.root.contains(document.activeElement)) return 1;

      const viewportHeight = window.visualViewport?.height ||
        window.innerHeight ||
        document.documentElement.clientHeight ||
        1;
      const range = this.getRevealRange(viewportHeight);

      return clamp(-range.start / Math.max(range.end - range.start, 1));
    }

    destroy() {
      window.removeEventListener('scroll', this.onScroll);
      this.reducedMotion.removeEventListener?.('change', this.onResize);
      this.mobileMedia?.removeEventListener?.('change', this.onResize);
      if (this.mobileOnly) {
        this.root.removeEventListener('focusin', this.onResize);
        this.root.removeEventListener('focusout', this.onResize);
      }

      if (this.usesSectionViewport) {
        this.section.removeEventListener('development:viewportchange', this.onResize);
      } else {
        window.removeEventListener('resize', this.onResize);
      }

      window.visualViewport?.removeEventListener('resize', this.onResize);

      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  }

  class DevelopmentScrollController {
    constructor(root, frameCoordinator) {
      this.root = root;
      this.frameCoordinator = frameCoordinator;
      this.background = root.querySelector('.development__background');
      this.hero = root.closest('main')?.querySelector('.hero-model-carousel-section');
      this.firstPanel = root.querySelector('[data-development-panel="first"]');
      this.statementPanel = root.querySelector('[data-development-panel="statement"]');
      this.secondPanel = root.querySelector('[data-development-panel="second"]');
      this.thirdPanel = root.querySelector('[data-development-panel="third"]');
      this.fourthPanel = root.querySelector('[data-development-panel="fourth"]');
      this.expertsHeadingTitle = root.querySelector('[data-development-experts-heading-title]');
      this.expertsHeadingCopy = root.querySelector('[data-development-experts-copy]');
      this.expertsCards = root.querySelector('[data-development-experts-cards]');
      this.expertCards = [...root.querySelectorAll('[data-development-expert-profile]')];
      this.statementQuotes = [...root.querySelectorAll('[data-development-statement-quote]')];
      this.statementCharacters = [...root.querySelectorAll('[data-development-statement-character]')];
      this.statementAccentWords = [...root.querySelectorAll('.development__statement-word--accent')];
      this.statementInsole = root.querySelector('[data-development-statement-insole]');
      this.statementVideo = root.querySelector('[data-development-statement-video]');
      this.statementVideoProgress = 0;
      this.statementVideoTargetProgress = 0;
      this.statementVideoFrameRate = CONFIG.statement.scrollVideo.defaultFrameRate;
      this.statementVideoObjectUrl = null;
      this.statementVideoBlobLoading = false;
      this.statementVideoFetchController = null;
      this.statementVideoSeekStartedAt = 0;
      this.statementVideoRetryTimer = null;
      this.statementVideoBlobAttempts = 0;
      this.statementVideoBlobRetryAt = 0;
      this.statementSecondaryInsole = root.querySelector('[data-development-statement-secondary-insole]');
      this.statementMobileInsoleMotion =
        this.statementPanel?.dataset.developmentStatementMobileInsoleMotion || 'compact-synced-tail';
      this.mobileBreakpoint = window.matchMedia('(max-width: 749px)');
      this.stableViewportMedia = window.matchMedia(
        '(max-width: 749px), (hover: none) and (pointer: coarse)'
      );
      this.parallax = root.querySelector('[data-development-parallax]');
      this.textureImages = [...root.querySelectorAll('[data-development-texture]')];
      this.transitionStyle = root.dataset.developmentTransitionStyle === 'style-transition-2'
        ? 'style-transition-2'
        : 'style-transition-1';
      this.transitionDuration = clamp(
        Number(root.dataset.developmentTransitionDuration || 750),
        100,
        5000
      );
      this.timedTransitions = {
        crossfade: this.createTimedTransition(),
        thirdTextureCrossfade: this.createTimedTransition(),
      };
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.needsMeasure = true;
      this.texturesReady = false;
      this.stylePropertyCache = new WeakMap();
      this.layoutViewportWidth = getLayoutViewportWidth();
      this.logicalViewportHeight = 0;
      this.stableViewportLayout = this.stableViewportMedia.matches;
      this.initialized = false;

      this.onScroll = this.onScroll.bind(this);
      this.onWindowResize = this.onWindowResize.bind(this);
      this.onVisualViewportResize = this.onVisualViewportResize.bind(this);
      this.onObservedResize = this.onObservedResize.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onStatementVideoLoadedMetadata = this.onStatementVideoLoadedMetadata.bind(this);
      this.onStatementVideoReady = this.onStatementVideoReady.bind(this);
      this.runFrame = this.runFrame.bind(this);
    }

    init() {
      if (
        !this.firstPanel ||
        !this.statementPanel ||
        !this.secondPanel ||
        !this.thirdPanel ||
        !this.fourthPanel
      ) return;

      this.initialized = true;
      this.captureViewportState();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onVisualViewportResize, { passive: true });
      window.addEventListener('pageshow', this.onPageShow);
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      this.stableViewportMedia.addEventListener?.('change', this.onWindowResize);
      this.frameCoordinator.add(this.runFrame);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onObservedResize);
        const stepsSidebar = this.thirdPanel.querySelector(
          '[data-development-steps-mode="scroll"] .development-steps__sidebar'
        );
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.firstPanel);
        this.resizeObserver.observe(this.statementPanel);
        this.resizeObserver.observe(this.secondPanel);
        this.resizeObserver.observe(this.thirdPanel);
        if (stepsSidebar) this.resizeObserver.observe(stepsSidebar);
        this.resizeObserver.observe(this.fourthPanel);
      }

      this.preloadTextures();
      this.initStatementVideo();
      this.schedule(true);
    }

    initStatementVideo() {
      if (!this.statementVideo) {
        return;
      }

      const defaultSource = this.statementVideo.dataset.developmentStatementVideoSource || '';
      const iosSource = this.statementVideo.dataset.developmentStatementVideoIosSource || '';
      const usesIOSSource = iosSource && shouldUseIOSAlphaVideo(this.statementVideo);
      const selectedSource = usesIOSSource ? iosSource : defaultSource;
      const configuredFrameRate = Number(
        usesIOSSource
          ? this.statementVideo.dataset.developmentStatementVideoIosFrameRate
          : this.statementVideo.dataset.developmentStatementVideoFrameRate
      );

      this.statementVideoFrameRate = Number.isFinite(configuredFrameRate) && configuredFrameRate > 0
        ? configuredFrameRate
        : CONFIG.statement.scrollVideo.defaultFrameRate;

      if (!selectedSource) {
        return;
      }

      this.statementVideo.src = selectedSource;
      this.statementVideoProgress = 0;
      this.statementVideoTargetProgress = 0;
      this.statementVideo.muted = true;
      this.statementVideo.playsInline = true;
      this.statementVideo.pause();

      this.statementVideo.addEventListener('loadedmetadata', this.onStatementVideoLoadedMetadata);
      this.statementVideo.addEventListener('durationchange', this.onStatementVideoLoadedMetadata);
      this.statementVideoReadyEvents = ['loadeddata', 'canplay', 'canplaythrough', 'progress', 'seeked', 'suspend', 'stalled'];
      this.statementVideoReadyEvents.forEach((event) => {
        this.statementVideo.addEventListener(event, this.onStatementVideoReady);
      });

      if (Number.isFinite(this.statementVideo.duration) && this.statementVideo.duration > 0) {
        this.onStatementVideoLoadedMetadata();
      } else {
        this.statementVideo.load();
      }

      if (this.statementVideo.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
        this.onStatementVideoReady();
      }
    }

    onStatementVideoLoadedMetadata() {
      if (!this.statementVideo) {
        return;
      }

      const duration = Number(this.statementVideo.duration);

      if (!Number.isFinite(duration) || duration <= 0) {
        return;
      }

      const videoAspectRatio = this.statementVideo.videoWidth / this.statementVideo.videoHeight;
      this.statementVideo.classList.toggle(
        'is-development-statement-video-landscape',
        Number.isFinite(videoAspectRatio) && videoAspectRatio > 1.2
      );
      this.schedule();
    }

    onStatementVideoReady() {
      if (!this.initialized) return;

      // canplaythrough is only an estimate and may never fire after an early
      // seek. Retry the latest scroll target on data arrival and seek completion.
      if (this.statementVideo.readyState >= 2 || this.statementVideo.buffered.length) {
        this.ensureStatementVideoSeekable();
      }
      this.schedule();
    }

    isStatementVideoSeekable() {
      if (!this.statementVideo) {
        return false;
      }

      const seekable = this.statementVideo.seekable;
      const duration = this.getStatementVideoDuration();

      for (let index = 0; index < seekable.length; index += 1) {
        if (duration > 0 && seekable.start(index) <= 0.001 && seekable.end(index) >= duration - 0.001) {
          return true;
        }
      }

      return false;
    }

    async ensureStatementVideoSeekable() {
      if (
        !this.statementVideo ||
        !this.initialized ||
        this.statementVideoObjectUrl ||
        this.statementVideoBlobLoading ||
        this.statementVideoBlobAttempts >= 2 ||
        performance.now() < this.statementVideoBlobRetryAt ||
        this.isStatementVideoSeekable()
      ) {
        return;
      }

      // A progressive response may still be expanding its usable range. Let it
      // finish; if it suspends with only a partial range, use the same fallback
      // as a response with no seekable range at all.
      const seekable = this.statementVideo.seekable;
      if (this.statementVideo.networkState === 2) {
        for (let index = 0; index < seekable.length; index += 1) {
          if (seekable.end(index) > seekable.start(index)) return;
        }
      }

      const source = this.statementVideo.currentSrc || this.statementVideo.getAttribute('src');

      if (!source || source.startsWith('blob:')) {
        return;
      }

      this.statementVideoBlobLoading = true;
      this.statementVideoBlobAttempts += 1;
      this.statementVideoFetchController = typeof AbortController === 'function'
        ? new AbortController()
        : null;

      try {
        const response = await fetch(source, {
          credentials: 'same-origin',
          signal: this.statementVideoFetchController?.signal,
        });

        if (!response.ok) {
          return;
        }

        const videoBlob = await response.blob();

        if (!this.initialized || !this.statementVideo) {
          return;
        }

        this.statementVideoObjectUrl = URL.createObjectURL(videoBlob);
        this.statementVideo.src = this.statementVideoObjectUrl;
        this.statementVideo.load();
      } catch (error) {
        if (error?.name !== 'AbortError') {
          // Native seeking remains available when the browser supports the original response.
        }
      } finally {
        this.statementVideoBlobLoading = false;
        this.statementVideoFetchController = null;
        this.statementVideoBlobRetryAt = performance.now() + 2000;
        if (this.initialized) this.scheduleStatementVideoRetry();
      }
    }

    getStatementVideoDuration() {
      if (!this.statementVideo) {
        return 0;
      }

      const liveDuration = Number(this.statementVideo.duration);

      if (Number.isFinite(liveDuration) && liveDuration > 0) {
        return liveDuration;
      }

      // Never seek against an invented duration before metadata, or a stale
      // duration while replacing a non-seekable response with a blob.
      return 0;
    }

    preloadTextures() {
      const decodePromises = this.textureImages.map((texture) => {
        if (texture.complete && texture.naturalWidth > 0) {
          return texture.decode?.().catch(() => undefined) || Promise.resolve();
        }

        return new Promise((resolve) => {
          texture.addEventListener('load', resolve, { once: true });
          texture.addEventListener('error', resolve, { once: true });
        }).then(() => texture.decode?.().catch(() => undefined));
      });

      Promise.allSettled(decodePromises).then(() => {
        this.texturesReady = true;
        this.schedule();
      });
    }

    onScroll() {
      this.schedule();
    }

    readViewportHeight() {
      if (this.stableViewportMedia.matches) {
        const backgroundHeight = this.background?.offsetHeight || 0;
        if (backgroundHeight > 0) return backgroundHeight;
      }

      return window.innerHeight || document.documentElement.clientHeight || 1;
    }

    getViewportHeight() {
      if (this.stableViewportMedia.matches && this.logicalViewportHeight > 0) {
        return this.logicalViewportHeight;
      }

      return this.readViewportHeight();
    }

    captureViewportState() {
      const width = getLayoutViewportWidth();
      const stable = this.stableViewportMedia.matches;
      const reset = Boolean(window.Shopify?.designMode) || !this.logicalViewportHeight ||
        stable !== this.stableViewportLayout ||
        Math.abs(width - this.layoutViewportWidth) > 1;

      this.layoutViewportWidth = width;
      this.stableViewportLayout = stable;

      if (!stable || reset) {
        this.root.style.removeProperty('--development-mobile-logical-viewport');
        this.root.style.removeProperty('--development-mobile-parallax-width');
        this.hero?.style.removeProperty('--development-native-hero-height');
        this.logicalViewportHeight = this.readViewportHeight();

        if (stable) {
          // Safari can recompute even lvh/svh during browser-chrome changes.
          // Freeze the measured CSS geometry, not just the JS timeline. A
          // ResizeObserver notification must not undo the height-only guard.
          const parallaxWidth = this.parallax?.offsetWidth;
          const heroHeight = this.hero?.offsetHeight;
          this.root.style.setProperty('--development-mobile-logical-viewport', `${this.logicalViewportHeight}px`);
          if (parallaxWidth) {
            this.root.style.setProperty('--development-mobile-parallax-width', `${parallaxWidth}px`);
          }
          if (heroHeight) {
            this.hero.style.setProperty('--development-native-hero-height', `${heroHeight}px`);
          }
        }
      }
    }

    notifyViewportChange() {
      this.root.dispatchEvent(new CustomEvent('development:viewportchange'));
    }

    onWindowResize() {
      const nextLayoutWidth = getLayoutViewportWidth();
      const nextStableViewportLayout = this.stableViewportMedia.matches;
      const geometryChanged = Boolean(window.Shopify?.designMode) ||
        !nextStableViewportLayout ||
        nextStableViewportLayout !== this.stableViewportLayout ||
        Math.abs(nextLayoutWidth - this.layoutViewportWidth) > 1;

      if (geometryChanged) {
        this.captureViewportState();
        this.schedule(true);
        this.notifyViewportChange();
        return;
      }

      // Mobile browser chrome changes the visual viewport height while the
      // sequence geometry remains anchored to stable viewport units. Render,
      // but never remap the timeline.
      this.schedule();
    }

    onVisualViewportResize() {
      if (window.Shopify?.designMode || !this.stableViewportMedia.matches) {
        this.onWindowResize();
        return;
      }

      this.schedule();
    }

    onObservedResize() {
      this.captureViewportState();
      this.schedule(true);
      this.notifyViewportChange();
    }

    onPageShow(event) {
      if (!event.persisted) return;

      this.timedTransitions.crossfade = this.createTimedTransition();
      this.timedTransitions.thirdTextureCrossfade = this.createTimedTransition();
      this.captureViewportState();
      this.schedule(true);
      this.notifyViewportChange();
    }

    onMotionPreferenceChange() {
      this.schedule(true);
    }

    schedule(measure = false) {
      this.needsMeasure = this.needsMeasure || measure;
      this.frameCoordinator.request();
    }

    setStyleProperty(element, property, value) {
      if (!element) return;

      const nextValue = String(value);
      let elementCache = this.stylePropertyCache.get(element);

      if (!elementCache) {
        elementCache = new Map();
        this.stylePropertyCache.set(element, elementCache);
      }

      if (elementCache.get(property) === nextValue) return;

      elementCache.set(property, nextValue);
      element.style.setProperty(property, nextValue);
    }

    toggleClass(element, className, active) {
      if (!element || element.classList.contains(className) === active) return;
      element.classList.toggle(className, active);
    }

    getScrollY() {
      return this.root.developmentScrollSmootherController?.getScrollY() ??
        (window.scrollY || document.documentElement.scrollTop);
    }

    runFrame(timestamp) {
      if (this.needsMeasure) {
        this.measure();
        this.needsMeasure = false;
      }

      this.render(timestamp);
      this.root.classList.add('is-development-scroll-enhanced');
    }

    createTimedTransition() {
      return {
        initialized: false,
        value: 0,
        target: 0,
        startValue: 0,
        startTime: 0,
        duration: this.transitionDuration,
      };
    }

    updateTimedTransition(state, target, timestamp) {
      if (!state.initialized) {
        state.initialized = true;
        state.value = target;
        state.target = target;
        state.startValue = target;
        state.startTime = timestamp;
        state.duration = 0;
        return { value: state.value, animating: false };
      }

      if (state.duration > 0) {
        const previousProgress = clamp((timestamp - state.startTime) / state.duration);
        state.value = mix(
          state.startValue,
          state.target,
          easeInOutCubic(previousProgress)
        );
      } else {
        state.value = state.target;
      }

      if (target !== state.target) {
        state.startValue = state.value;
        state.target = target;
        state.startTime = timestamp;
        state.duration = this.transitionDuration * Math.abs(target - state.startValue);
      }

      const progress = state.duration > 0
        ? clamp((timestamp - state.startTime) / state.duration)
        : 1;
      state.value = mix(state.startValue, state.target, easeInOutCubic(progress));

      return {
        value: state.value,
        animating: progress < 1,
      };
    }

    measure() {
      const scrollY = this.getScrollY();
      const firstTop = this.firstPanel.getBoundingClientRect().top + scrollY;
      const statementTop = this.statementPanel.getBoundingClientRect().top + scrollY;
      const secondTop = this.secondPanel.getBoundingClientRect().top + scrollY;
      const thirdTop = this.thirdPanel.getBoundingClientRect().top + scrollY;
      const fourthTop = this.fourthPanel.getBoundingClientRect().top + scrollY;
      const expertsCardsTop = this.expertsCards
        ? this.expertsCards.getBoundingClientRect().top + scrollY
        : null;
      const firstTransitionDistance = Math.max(thirdTop - secondTop, 1);
      const secondTransitionDistance = Math.max(fourthTop - thirdTop, 1);
      const viewportHeight = this.getViewportHeight();
      const stepsScroller = this.thirdPanel.querySelector('[data-development-steps-mode="scroll"]');
      const stepsSidebar = stepsScroller?.querySelector('.development-steps__sidebar');
      const stepsEyebrow = stepsSidebar?.querySelector('.development__eyebrow');

      this.logicalViewportHeight = viewportHeight;
      this.timelineStart = firstTop;
      this.timelineEnd = Math.max(fourthTop, firstTop + 1);
      this.statementStart = statementTop;
      this.statementEnd = statementTop + Math.max(this.statementPanel.offsetHeight - viewportHeight, 1);
      this.statementFadeStart =
        this.statementEnd - viewportHeight * CONFIG.statementToExperts.statementFadeStartBeforeStatementEndViewportY;
      this.statementFadeEnd =
        this.statementEnd + viewportHeight * CONFIG.statementToExperts.statementFadeEndAfterStatementEndViewportY;
      this.statementToExpertsStart =
        this.statementEnd + viewportHeight * CONFIG.statementToExperts.startAfterStatementEndViewportY;
      this.statementToExpertsEnd =
        this.statementEnd + viewportHeight * CONFIG.statementToExperts.endAfterStatementEndViewportY;
      this.viewportHeight = viewportHeight;
      this.crossfadeStart = secondTop + firstTransitionDistance * clamp(CONFIG.crossfade.start);
      this.crossfadeEnd = secondTop + firstTransitionDistance * clamp(CONFIG.crossfade.end);
      if (stepsEyebrow) {
        const eyebrowRect = stepsEyebrow.getBoundingClientRect();
        const revealOffset = Number.parseFloat(
          window.getComputedStyle(stepsEyebrow).getPropertyValue('--development-reveal-offset')
        ) || 0;
        const eyebrowCenter = eyebrowRect.top + scrollY + eyebrowRect.height / 2 - revealOffset;
        // Finish the blue-to-dark crossfade as the "3 Easy Steps" eyebrow
        // reaches the middle of the viewport, instead of at the panel boundary.
        this.crossfadeEnd = eyebrowCenter - viewportHeight / 2;
      }
      this.thirdTextureCrossfadeStart =
        thirdTop + secondTransitionDistance * clamp(CONFIG.thirdTextureCrossfade.start);
      this.thirdTextureCrossfadeEnd =
        thirdTop + secondTransitionDistance * clamp(CONFIG.thirdTextureCrossfade.end);

      // On the homepage the final steps card shares a sticky sidebar. Hold the
      // dark texture until that sidebar has actually released, rather than
      // starting the crossfade halfway through the steps sequence.
      if (!this.mobileBreakpoint.matches && stepsScroller && stepsSidebar) {
        const stepsTop = stepsScroller.getBoundingClientRect().top + scrollY;
        const stickyOffset = Math.max((viewportHeight - stepsSidebar.offsetHeight) / 2, 0) +
          DEVELOPMENT_STEPS_STICKY_HEADER_COMPENSATION;
        stepsSidebar.style.setProperty('--development-steps-sticky-offset', `${stickyOffset}px`);
        const stickyRelease =
          stepsTop + stepsScroller.offsetHeight - stepsSidebar.offsetHeight - stickyOffset;

        this.thirdTextureCrossfadeStart = Math.max(stickyRelease + 1, thirdTop);
        this.thirdTextureCrossfadeEnd = Math.max(fourthTop, this.thirdTextureCrossfadeStart + 1);
      }
      if (this.mobileBreakpoint.matches && this.thirdPanel.querySelector('.development-steps--mobile-stack')) {
        // Keep all three mobile cards on the dark texture; transition as the next section enters.
        this.thirdTextureCrossfadeStart = Math.max(thirdTop, fourthTop - viewportHeight * 0.75);
        this.thirdTextureCrossfadeEnd = Math.max(this.thirdTextureCrossfadeStart + 1, fourthTop - viewportHeight * 0.25);
      }
      if (this.crossfadeEnd <= this.crossfadeStart) {
        this.crossfadeEnd = this.crossfadeStart + 1;
      }

      if (this.thirdTextureCrossfadeEnd <= this.thirdTextureCrossfadeStart) {
        this.thirdTextureCrossfadeEnd = this.thirdTextureCrossfadeStart + 1;
      }

      this.crossfadeTrigger = stepsEyebrow
        ? this.crossfadeEnd
        : Number.isFinite(expertsCardsTop)
          ? expertsCardsTop - viewportHeight * clamp(CONFIG.timedTransition.expertsCardsViewportY)
          : mix(this.crossfadeStart, this.crossfadeEnd, 0.5);
      this.thirdTextureCrossfadeTrigger = mix(
        this.thirdTextureCrossfadeStart,
        this.thirdTextureCrossfadeEnd,
        0.5
      );
    }

    render(timestamp = performance.now()) {
      if (typeof this.timelineStart !== 'number') return;

      const scrollY = this.getScrollY();
      const parallaxProgress = clamp(
        (scrollY - this.timelineStart) / (this.timelineEnd - this.timelineStart)
      );
      const statementProgress = progressBetween(scrollY, this.statementStart, this.statementEnd);
      const releaseTailConfig = this.statementMobileInsoleMotion === 'compact-synced-tail'
        ? CONFIG.statement.mobileCompactSyncedTail
        : CONFIG.statement.mobileCompact;
      const statementReleaseTailProgress = progressBetween(
        scrollY,
        this.statementEnd,
        this.statementEnd +
          this.viewportHeight * releaseTailConfig.releaseTailScrollViewport
      );
      const parallaxY = CONFIG.initialTextureY -
        CONFIG.parallaxDistance * CONFIG.parallaxIntensity * parallaxProgress;

      let crossfadeProgress;
      let thirdTextureCrossfadeProgress;
      let timedTransitionAnimating = false;

      if (this.transitionStyle === 'style-transition-2' && this.texturesReady) {
        const crossfade = this.updateTimedTransition(
          this.timedTransitions.crossfade,
          scrollY >= this.crossfadeTrigger ? 1 : 0,
          timestamp
        );
        const thirdTextureCrossfade = this.updateTimedTransition(
          this.timedTransitions.thirdTextureCrossfade,
          scrollY >= this.thirdTextureCrossfadeTrigger ? 1 : 0,
          timestamp
        );
        crossfadeProgress = crossfade.value;
        thirdTextureCrossfadeProgress = thirdTextureCrossfade.value;
        timedTransitionAnimating = crossfade.animating || thirdTextureCrossfade.animating;
      } else {
        crossfadeProgress = clamp(
          (scrollY - this.crossfadeStart) / (this.crossfadeEnd - this.crossfadeStart)
        );
        thirdTextureCrossfadeProgress = clamp(
          (scrollY - this.thirdTextureCrossfadeStart) /
            (this.thirdTextureCrossfadeEnd - this.thirdTextureCrossfadeStart)
        );
      }

      if (this.reducedMotion.matches) {
        crossfadeProgress = scrollY >= (
          this.transitionStyle === 'style-transition-2'
            ? this.crossfadeTrigger
            : this.crossfadeEnd
        ) ? 1 : 0;
        thirdTextureCrossfadeProgress =
          scrollY >= (
            this.transitionStyle === 'style-transition-2'
              ? this.thirdTextureCrossfadeTrigger
              : this.thirdTextureCrossfadeEnd
          ) ? 1 : 0;
        timedTransitionAnimating = false;
      }

      if (!this.texturesReady) {
        crossfadeProgress = 0;
        thirdTextureCrossfadeProgress = 0;
      }

      const opacity = CONFIG.textureOpacity;
      const texture1Opacity = mix(opacity.texture1.from, opacity.texture1.to, crossfadeProgress);
      const texture2BaseOpacity = mix(
        opacity.texture2.from,
        opacity.texture2.to,
        crossfadeProgress
      );
      const texture2Opacity = mix(texture2BaseOpacity, 0, thirdTextureCrossfadeProgress);
      const texture3Opacity = mix(
        opacity.texture3.from,
        opacity.texture3.to,
        thirdTextureCrossfadeProgress
      );
      const featuresTextChannel = Math.round(mix(255, 0, thirdTextureCrossfadeProgress));

      this.setStyleProperty(
        this.parallax,
        '--development-parallax-y',
        `${this.reducedMotion.matches ? CONFIG.initialTextureY : parallaxY.toFixed(2)}px`
      );
      this.setStyleProperty(
        this.textureImages[0],
        '--development-texture-1-opacity',
        texture1Opacity.toFixed(4)
      );
      this.setStyleProperty(
        this.textureImages[1],
        '--development-texture-2-opacity',
        texture2Opacity.toFixed(4)
      );
      this.setStyleProperty(
        this.textureImages[2],
        '--development-texture-3-opacity',
        texture3Opacity.toFixed(4)
      );
      this.setStyleProperty(
        this.root,
        '--development-features-text-color',
        `rgb(${featuresTextChannel}, ${featuresTextChannel}, ${featuresTextChannel})`
      );
      this.renderStatement(statementProgress, statementReleaseTailProgress);
      this.updateStatementVideoScrub();
      this.renderStatementToExperts(scrollY);

      if (timedTransitionAnimating) {
        this.schedule();
      }
    }

    renderStatementToExperts(scrollY) {
      const config = CONFIG.statementToExperts;

      if (this.reducedMotion.matches) {
        this.statementQuotes.forEach((quote) => {
          this.setStyleProperty(quote, '--development-statement-quote-opacity', '1');
          this.setStyleProperty(quote, '--development-statement-quote-y', '0px');
        });
        this.setStyleProperty(this.expertsHeadingTitle, '--development-experts-heading-opacity', '1');
        this.setStyleProperty(this.expertsHeadingTitle, '--development-experts-heading-y', '0px');
        this.setStyleProperty(this.expertsHeadingCopy, '--development-experts-copy-opacity', '1');
        this.setStyleProperty(this.expertsHeadingCopy, '--development-experts-copy-y', '0px');
        this.expertCards.forEach((card) => {
          this.setStyleProperty(card, '--development-expert-card-opacity', '1');
          this.setStyleProperty(card, '--development-expert-card-y', '0px');
        });
        return;
      }

      const progress = progressBetween(
        scrollY,
        this.statementToExpertsStart,
        this.statementToExpertsEnd
      );
      const statementFade = progressBetween(scrollY, this.statementFadeStart, this.statementFadeEnd);
      const easedStatementLift = easeInOutCubic(statementFade);
      const expertsY = mix(
        config.expertsTranslateStartViewportY * this.viewportHeight,
        0,
        easeOutCubic(progress)
      );
      const headingProgress = progressBetween(progress, config.headingStart, config.headingEnd);
      const copyProgress = progressBetween(progress, config.copyStart, config.copyEnd);

      this.statementQuotes.forEach((quote) => {
        this.setStyleProperty(
          quote,
          '--development-statement-quote-opacity',
          (1 - statementFade).toFixed(4)
        );
        this.setStyleProperty(
          quote,
          '--development-statement-quote-y',
          `${mix(0, -42, easedStatementLift).toFixed(2)}px`
        );
      });

      const easedHeadingProgress = easeInOutCubic(headingProgress);
      const easedCopyProgress = easeInOutCubic(copyProgress);
      this.setStyleProperty(
        this.expertsHeadingTitle,
        '--development-experts-heading-opacity',
        easedHeadingProgress.toFixed(4)
      );
      this.setStyleProperty(
        this.expertsHeadingTitle,
        '--development-experts-heading-y',
        `${(expertsY + mix(36, 0, easedHeadingProgress)).toFixed(2)}px`
      );
      this.setStyleProperty(
        this.expertsHeadingCopy,
        '--development-experts-copy-opacity',
        easedCopyProgress.toFixed(4)
      );
      this.setStyleProperty(
        this.expertsHeadingCopy,
        '--development-experts-copy-y',
        `${(expertsY + mix(32, 0, easedCopyProgress)).toFixed(2)}px`
      );

      this.expertCards.forEach((card, index) => {
        const profileIndex = config.cardOrder.indexOf(card.dataset.developmentExpertProfile || '');
        const isMobile = this.mobileBreakpoint.matches;
        const sequenceIndex = isMobile
          ? 0
          : (profileIndex >= 0 ? profileIndex : index);
        const cardStart = isMobile ? config.cardStart : config.desktopCardStart;
        const cardStep = isMobile ? config.cardStep : config.desktopCardStep;
        const cardDuration = isMobile ? config.cardDuration : config.desktopCardDuration;
        const cardProgress = progressBetween(
          progress,
          cardStart + cardStep * sequenceIndex,
          cardStart + cardStep * sequenceIndex + cardDuration
        );
        const easedCardProgress = easeInOutCubic(cardProgress);

        this.setStyleProperty(card, '--development-expert-card-opacity', easedCardProgress.toFixed(4));
        this.setStyleProperty(
          card,
          '--development-expert-card-y',
          `${(expertsY + mix(38, 0, easedCardProgress)).toFixed(2)}px`
        );
      });
    }

    renderStatement(progress, releaseTailProgress = 0) {
      const statement = CONFIG.statement;
      const extraDistance = !this.mobileBreakpoint.matches && this.statementVideo && !this.reducedMotion.matches
        ? statement.scrollVideo.desktopExtraScrollViewport * this.viewportHeight
        : 0;
      const { textProgress, mediaProgress } = extendStatementVideoProgress(
        progress, this.statementEnd - this.statementStart, extraDistance
      );

      const accentGlowProgress = this.reducedMotion.matches ? 0 : easeInOutCubic(textProgress);
      this.statementAccentWords.forEach((word) => {
        this.setStyleProperty(
          word,
          '--development-statement-glow-blur',
          `${(accentGlowProgress * 8).toFixed(2)}px`
        );
        this.setStyleProperty(
          word,
          '--development-statement-glow-opacity',
          (accentGlowProgress * 0.72).toFixed(4)
        );
        this.setStyleProperty(
          word,
          '--development-statement-glow-outer-blur',
          `${(accentGlowProgress * 28).toFixed(2)}px`
        );
        this.setStyleProperty(
          word,
          '--development-statement-glow-outer-opacity',
          (accentGlowProgress * 0.34).toFixed(4)
        );
      });

      if (this.reducedMotion.matches) {
        this.statementCharacters.forEach((character) => {
          this.toggleClass(character, 'is-development-character-animating', false);
          this.setStyleProperty(character, '--development-character-opacity', '1');
          this.setStyleProperty(character, '--development-character-y', '0px');
        });
        this.toggleClass(this.statementInsole, 'is-development-insole-animating', false);
        this.toggleClass(this.statementSecondaryInsole, 'is-development-insole-animating', false);
        this.setStyleProperty(this.statementInsole, '--development-insole-opacity', '0');
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-opacity',
          '0'
        );
        this.resetStatementVideo();
        return;
      }

      const useCompactSyncedTailMobileMotion =
        this.mobileBreakpoint.matches && this.statementMobileInsoleMotion === 'compact-synced-tail';
      const useCompactSyncedMobileMotion =
        this.mobileBreakpoint.matches && (
          this.statementMobileInsoleMotion === 'compact-synced' ||
          useCompactSyncedTailMobileMotion
        );
      const useCompactMobileMotion =
        this.mobileBreakpoint.matches && (
          this.statementMobileInsoleMotion === 'compact' || useCompactSyncedMobileMotion
        );
      const syncedConfig = useCompactSyncedTailMobileMotion
        ? statement.mobileCompactSyncedTail
        : statement.mobileCompactSynced;
      const characterRevealStart = useCompactSyncedMobileMotion
        ? syncedConfig.characterRevealStart
        : statement.characterRevealStart;
      const characterRevealEnd = useCompactSyncedMobileMotion
        ? syncedConfig.characterRevealEnd
        : statement.characterRevealEnd;
      const revealSpan = characterRevealEnd - characterRevealStart;
      const characterStep = revealSpan / (this.statementCharacters.length + 1);
      const characterFadeDuration = characterStep * statement.characterFadeOverlap;

      this.statementCharacters.forEach((character, index) => {
        const characterStart = characterRevealStart + characterStep * index;
        const characterProgress = progressBetween(
          textProgress,
          characterStart,
          characterStart + characterFadeDuration
        );
        const easedCharacterProgress = easeOutCubic(characterProgress);
        const prepareStart = Math.max(characterRevealStart, characterStart - characterStep * 0.75);
        const shouldPrepareCharacter = textProgress >= prepareStart &&
          textProgress <= characterStart + characterFadeDuration;

        this.toggleClass(
          character,
          'is-development-character-animating',
          shouldPrepareCharacter
        );
        this.setStyleProperty(
          character,
          '--development-character-opacity',
          characterProgress.toFixed(4)
        );
        this.setStyleProperty(
          character,
          '--development-character-y',
          `${mix(16, 0, easedCharacterProgress).toFixed(2)}px`
        );
      });

      const insoleStart = useCompactSyncedMobileMotion
        ? syncedConfig.insoleStart
        : statement.insoleStart;
      const insoleEnd = useCompactSyncedMobileMotion
        ? syncedConfig.insoleEnd
        : statement.insoleEnd;
      const insoleProgress = progressBetween(mediaProgress, insoleStart, insoleEnd);
      const insoleEnterViewportY = useCompactMobileMotion
        ? statement.mobileCompact.insoleEnterViewportY
        : statement.insoleEnterViewportY;
      const insoleExitViewportY = useCompactMobileMotion
        ? statement.mobileCompact.insoleExitViewportY
        : statement.insoleExitViewportY;
      const useReleaseTail = useCompactMobileMotion && (
        !useCompactSyncedMobileMotion || useCompactSyncedTailMobileMotion
      );
      const releaseTailMotionConfig = useCompactSyncedTailMobileMotion
        ? statement.mobileCompactSyncedTail
        : statement.mobileCompact;
      const easedReleaseTailProgress = useReleaseTail
        ? easeOutCubic(releaseTailProgress)
        : 0;
      let easedInsoleProgress = easeInOutCubic(insoleProgress);
      let insoleY;
      let insoleRotation;
      let insoleScale;
      let insoleOpacity;

      if (this.statementVideo) {
        const videoTimeline = statement.scrollVideo;
        const enterProgress = progressBetween(insoleProgress, 0, videoTimeline.motionEnterEnd);
        const exitProgress = progressBetween(insoleProgress, videoTimeline.motionExitStart, 1);
        const scrubProgress = progressBetween(
          insoleProgress,
          videoTimeline.enterEnd,
          videoTimeline.scrubEnd
        );
        const easedEnterProgress = easeInOutCubic(enterProgress);
        const easedExitProgress = easeInOutCubic(exitProgress);
        const settledY = 0;
        const settledRotation = videoTimeline.settledRotation;
        const settledScale = videoTimeline.settledScale;

        this.syncStatementVideoToProgress(scrubProgress);

        if (insoleProgress < videoTimeline.motionEnterEnd) {
          insoleY = mix(
            insoleEnterViewportY * this.viewportHeight,
            settledY,
            easedEnterProgress
          );
          insoleRotation = mix(
            statement.insoleRotationStart,
            settledRotation,
            easedEnterProgress
          );
          insoleScale = mix(statement.insoleScaleStart, settledScale, easedEnterProgress);
          easedInsoleProgress = easedEnterProgress * videoTimeline.motionEnterEnd;
        } else if (insoleProgress <= videoTimeline.motionExitStart) {
          insoleY = settledY;
          insoleRotation = settledRotation;
          insoleScale = settledScale;
          easedInsoleProgress = videoTimeline.motionEnterEnd;
        } else {
          insoleY = mix(
            settledY,
            insoleExitViewportY * this.viewportHeight,
            easedExitProgress
          ) +
            releaseTailMotionConfig.releaseTailLiftViewportY *
              this.viewportHeight * easedReleaseTailProgress;
          insoleRotation = mix(
            settledRotation,
            statement.insoleRotationEnd,
            easedExitProgress
          ) + releaseTailMotionConfig.releaseTailRotation * easedReleaseTailProgress;
          insoleScale = mix(settledScale, statement.insoleScaleEnd, easedExitProgress);
          easedInsoleProgress = mix(videoTimeline.motionEnterEnd, 1, easedExitProgress);
        }

        const fadeIn = progressBetween(insoleProgress, 0, videoTimeline.fadeInEnd);
        const fadeOutStart = useCompactSyncedTailMobileMotion &&
          Number.isFinite(releaseTailMotionConfig.fadeOutStart)
          ? releaseTailMotionConfig.fadeOutStart
          : videoTimeline.fadeOutStart;
        const fadeOut = 1 - progressBetween(insoleProgress, fadeOutStart, 1);
        insoleOpacity = Math.min(fadeIn, fadeOut);
      } else {
        insoleY = mix(
          insoleEnterViewportY * this.viewportHeight,
          insoleExitViewportY * this.viewportHeight,
          easedInsoleProgress
        ) +
          releaseTailMotionConfig.releaseTailLiftViewportY *
            this.viewportHeight * easedReleaseTailProgress;
        insoleRotation = mix(
          statement.insoleRotationStart,
          statement.insoleRotationEnd,
          easedInsoleProgress
        ) + releaseTailMotionConfig.releaseTailRotation * easedReleaseTailProgress;
        insoleScale = useCompactMobileMotion
          ? statement.mobileCompact.insoleScale
          : mix(
            statement.insoleScaleStart,
            statement.insoleScaleEnd,
            easedInsoleProgress
          );
        const insoleFadeInStart = useCompactSyncedMobileMotion
          ? syncedConfig.insoleFadeInStart
          : statement.insoleFadeInStart;
        const insoleFadeInEnd = useCompactSyncedMobileMotion
          ? syncedConfig.insoleFadeInEnd
          : statement.insoleFadeInEnd;
        const fadeIn = progressBetween(progress, insoleFadeInStart, insoleFadeInEnd);
        const fadeOut = useCompactMobileMotion && statement.mobileCompact.retainEndOpacity
          ? 1
          : 1 - progressBetween(progress, statement.insoleFadeOutStart, statement.insoleFadeOutEnd);
        insoleOpacity = Math.min(fadeIn, fadeOut);
      }

      const shouldPrepareInsole = mediaProgress > Math.max(insoleStart - 0.02, 0) && (
        mediaProgress < insoleEnd ||
        (useReleaseTail && releaseTailProgress < 1)
      );
      this.toggleClass(
        this.statementInsole,
        'is-development-insole-animating',
        shouldPrepareInsole
      );
      this.setStyleProperty(
        this.statementInsole,
        '--development-insole-y',
        `${insoleY.toFixed(2)}px`
      );
      this.setStyleProperty(
        this.statementInsole,
        '--development-insole-rotation',
        `${insoleRotation.toFixed(2)}deg`
      );
      this.setStyleProperty(
        this.statementInsole,
        '--development-insole-scale',
        insoleScale.toFixed(4)
      );
      this.setStyleProperty(
        this.statementInsole,
        '--development-insole-opacity',
        insoleOpacity.toFixed(4)
      );

      if (this.statementSecondaryInsole && this.statementVideo) {
        this.toggleClass(this.statementSecondaryInsole, 'is-development-insole-animating', false);
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-opacity',
          '0'
        );
      } else if (this.statementSecondaryInsole) {
        const animation2 = statement.animation2;
        const viewportWidth = window.innerWidth;
        const secondStartX = this.mobileBreakpoint.matches
          ? animation2.mobileSecondInsoleStartViewportX
          : animation2.secondInsoleStartViewportX;
        const secondEndX = this.mobileBreakpoint.matches
          ? animation2.mobileSecondInsoleEndViewportX
          : animation2.secondInsoleEndViewportX;
        const secondOffsetY = this.mobileBreakpoint.matches
          ? animation2.mobileSecondInsoleOffsetViewportY
          : animation2.secondInsoleOffsetViewportY;
        const secondRotationStart = this.mobileBreakpoint.matches
          ? animation2.mobileSecondInsoleRotationStart
          : animation2.secondInsoleRotationStart;
        const secondRotationEnd = this.mobileBreakpoint.matches
          ? animation2.mobileSecondInsoleRotationEnd
          : animation2.secondInsoleRotationEnd;
        const secondInsoleX = mix(
          secondStartX * viewportWidth,
          secondEndX * viewportWidth,
          easedInsoleProgress
        );
        const secondInsoleY = insoleY + secondOffsetY * this.viewportHeight;
        const secondInsoleRotation = mix(
          secondRotationStart,
          secondRotationEnd,
          easedInsoleProgress
        ) + releaseTailMotionConfig.releaseTailRotation * easedReleaseTailProgress * 0.5;
        const secondInsoleScale = useCompactMobileMotion
          ? animation2.mobileSecondInsoleScale
          : mix(
            animation2.secondInsoleScaleStart,
            animation2.secondInsoleScaleEnd,
            easedInsoleProgress
          );

        this.toggleClass(
          this.statementSecondaryInsole,
          'is-development-insole-animating',
          shouldPrepareInsole
        );
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-x',
          `${secondInsoleX.toFixed(2)}px`
        );
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-y',
          `${secondInsoleY.toFixed(2)}px`
        );
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-rotation',
          `${secondInsoleRotation.toFixed(2)}deg`
        );
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-scale',
          secondInsoleScale.toFixed(4)
        );
        this.setStyleProperty(
          this.statementSecondaryInsole,
          '--development-second-insole-opacity',
          insoleOpacity.toFixed(4)
        );
      }
    }

    syncStatementVideoToProgress(progress) {
      if (!this.statementVideo) {
        return;
      }

      this.statementVideoTargetProgress = clamp(progress);
    }

    scheduleStatementVideoRetry() {
      if (!this.initialized || this.statementVideoRetryTimer !== null) return;

      // A low-frequency watchdog covers browsers that omit a readiness event.
      // It stops once the latest target has decoded; it never drives page scroll.
      this.statementVideoRetryTimer = window.setTimeout(() => {
        this.statementVideoRetryTimer = null;
        this.onStatementVideoReady();
      }, 250);
    }

    updateStatementVideoScrub() {
      this.seekStatementVideoToProgress(this.statementVideoTargetProgress);
    }

    statementVideoRangeContains(ranges, time) {
      for (let index = 0; index < ranges.length; index += 1) {
        if (ranges.start(index) <= time && ranges.end(index) > time) return true;
      }
      return false;
    }

    seekStatementVideoToProgress(progress) {
      if (!this.statementVideo) {
        return;
      }

      const nextProgress = clamp(progress);
      const duration = this.getStatementVideoDuration();

      if (!duration || this.statementVideo.readyState === 0) {
        if (nextProgress > 0) this.scheduleStatementVideoRetry();
        return;
      }

      const frameDuration = 1 / this.statementVideoFrameRate;
      const maxTime = Math.max(duration - 0.001, 0);
      const rawTargetTime = maxTime * nextProgress;
      const targetTime = nextProgress >= 1
        ? maxTime
        : Math.min(Math.round(rawTargetTime / frameDuration) * frameDuration, maxTime);
      const currentTime = Number(this.statementVideo.currentTime) || 0;

      const atTarget = Math.abs(currentTime - targetTime) < frameDuration * 0.5;

      if (!this.statementVideo.seeking && this.statementVideo.readyState >= 2 && atTarget) {
        // currentTime changes synchronously on assignment, before decoding.
        // Only acknowledge a completed seek with a usable frame.
        this.statementVideoProgress = nextProgress;
        window.clearTimeout(this.statementVideoRetryTimer);
        this.statementVideoRetryTimer = null;
        return;
      }

      this.statementVideo.pause();
      this.scheduleStatementVideoRetry();

      if (this.statementVideo.seeking) {
        // Coalesce rapid updates behind the pending seek. A reversal into an
        // already buffered range can rescue an outstanding network-bound seek.
        const canRecover = !atTarget &&
          performance.now() - this.statementVideoSeekStartedAt > 750 &&
          this.statementVideoRangeContains(this.statementVideo.buffered, targetTime);
        if (!canRecover) return;
      }

      if (!this.statementVideoRangeContains(this.statementVideo.seekable, targetTime)) {
        // In particular, Shopify CLI can return 200 without byte-range support.
        // Seeking into its [0, 0] range silently clamps to the first frame.
        if (this.statementVideo.readyState >= 2 || this.statementVideo.buffered.length) {
          this.ensureStatementVideoSeekable();
        }
        return;
      }

      if (atTarget) return;

      try {
        this.statementVideoSeekStartedAt = performance.now();
        this.statementVideo.currentTime = targetTime;
      } catch (error) {
        // Keep the requested progress pending until a media event or retry.
      }
    }

    resetStatementVideoMedia() {
      if (!this.statementVideo) {
        return;
      }

      this.statementVideo.pause();
      this.seekStatementVideoToProgress(0);
    }

    resetStatementVideo() {
      if (!this.statementVideo) {
        return;
      }

      this.statementVideo.pause();
      this.statementVideoProgress = 0;
      this.statementVideoTargetProgress = 0;

      this.resetStatementVideoMedia();
    }

    destroy() {
      this.initialized = false;
      this.root.style.removeProperty('--development-mobile-logical-viewport');
      this.root.style.removeProperty('--development-mobile-parallax-width');
      this.hero?.style.removeProperty('--development-native-hero-height');
      this.root.classList.remove('is-development-scroll-enhanced');
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onWindowResize);
      window.visualViewport?.removeEventListener('resize', this.onVisualViewportResize);
      window.removeEventListener('pageshow', this.onPageShow);
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      this.stableViewportMedia.removeEventListener?.('change', this.onWindowResize);
      this.statementVideo?.removeEventListener('loadedmetadata', this.onStatementVideoLoadedMetadata);
      this.statementVideo?.removeEventListener('durationchange', this.onStatementVideoLoadedMetadata);
      this.statementVideoReadyEvents?.forEach((event) => {
        this.statementVideo.removeEventListener(event, this.onStatementVideoReady);
      });
      window.clearTimeout(this.statementVideoRetryTimer);
      this.statementVideoRetryTimer = null;
      this.statementVideo?.pause();
      this.statementVideoFetchController?.abort();

      if (this.statementVideoObjectUrl) {
        URL.revokeObjectURL(this.statementVideoObjectUrl);
        this.statementVideoObjectUrl = null;
      }
      this.statementCharacters.forEach((character) => {
        character.classList.remove('is-development-character-animating');
      });
      this.statementInsole?.classList.remove('is-development-insole-animating');
      this.statementSecondaryInsole?.classList.remove('is-development-insole-animating');
      this.resizeObserver?.disconnect();
      this.frameCoordinator.remove(this.runFrame);
    }
  }

  class DevelopmentGlassCardController {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-development]');
      this.parallax = this.section?.querySelector('[data-development-parallax]');
      this.canvas = root.querySelector('[data-development-glass-canvas]');
      this.source = root.querySelector('[data-development-glass-source]');
      this.strength = clamp(Number(root.dataset.developmentGlassStrength || 65) / 100);
      this.frame = null;
      this.started = false;
      this.destroyed = false;

      this.onResize = this.onResize.bind(this);
      this.onSourceLoad = this.onSourceLoad.bind(this);
      this.scheduleRender = this.scheduleRender.bind(this);
    }

    init() {
      if (!this.canvas || !this.source || !this.parallax) return;

      if (this.source.complete && this.source.naturalWidth > 0) {
        this.onSourceLoad();
      } else {
        this.source.addEventListener('load', this.onSourceLoad, { once: true });
      }
    }

    onSourceLoad() {
      if (this.started || this.destroyed || !this.createContext()) return;
      this.started = true;

      try {
        const gl = this.gl;
        gl.bindTexture(gl.TEXTURE_2D, this.texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.source);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      } catch (error) {
        this.releaseContext();
        return;
      }

      window.addEventListener('scroll', this.scheduleRender, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.parallax);
      }

      this.resize();
      this.render();
    }

    createContext() {
      const gl = this.canvas.getContext('webgl', {
        alpha: true,
        antialias: false,
        premultipliedAlpha: true,
      });
      if (!gl) return false;

      const vertexSource = `
        attribute vec2 position;

        void main() {
          gl_Position = vec4(position, 0.0, 1.0);
        }
      `;
      const fragmentSource = `
        precision mediump float;

        uniform vec2 iResolution;
        uniform vec4 uCardRect;
        uniform vec4 uTextureRect;
        uniform sampler2D iChannel0;
        uniform float uStrength;

        float roundedRectSdf(vec2 point, vec2 halfSize, float radius) {
          vec2 distance = abs(point) - halfSize + radius;
          return min(max(distance.x, distance.y), 0.0) +
            length(max(distance, 0.0)) - radius;
        }

        void mainImage(out vec4 fragColor, in vec2 fragCoord) {
          const float SAMPLE_RANGE = 4.0;
          const float SAMPLE_OFFSET = 1.0;

          vec2 localUv = vec2(
            fragCoord.x / iResolution.x,
            1.0 - fragCoord.y / iResolution.y
          );
          vec2 cardSize = uCardRect.zw;
          vec2 localPoint = localUv * cardSize;
          vec2 cardCenter = localPoint - cardSize * 0.5;
          float radius = min(32.0, min(cardSize.x, cardSize.y) * 0.18);
          float signedDistance = roundedRectSdf(cardCenter, cardSize * 0.5, radius);

          if (signedDistance > 0.0) {
            fragColor = vec4(0.0);
            return;
          }

          float edgeDistance = -signedDistance;
          float effectStrength = clamp(uStrength / 0.65, 0.1, 1.5);
          float edgeRange = max(42.0, min(cardSize.x, cardSize.y) * 0.15);
          float edgeZone = 1.0 - smoothstep(0.0, edgeRange, edgeDistance);
          vec2 outwardNormal = normalize(cardCenter + vec2(0.0001));
          float refractionPixels = (6.0 + 18.0 * effectStrength) * pow(edgeZone, 1.35);
          vec2 backgroundUv = (
            uCardRect.xy + localPoint - uTextureRect.xy
          ) / uTextureRect.zw;
          vec2 refractedUv = backgroundUv -
            outwardNormal * refractionPixels / uTextureRect.zw;
          float blurPixels = (2.5 + edgeZone * 2.5) * effectStrength;

          vec4 blurred = vec4(0.0);
          float total = 0.0;
          for (float x = -SAMPLE_RANGE; x <= SAMPLE_RANGE; x++) {
            for (float y = -SAMPLE_RANGE; y <= SAMPLE_RANGE; y++) {
              vec2 offset = vec2(x, y) * SAMPLE_OFFSET * blurPixels / uTextureRect.zw;
              blurred += texture2D(iChannel0, refractedUv + offset);
              total += 1.0;
            }
          }
          blurred /= total;

          vec4 base = texture2D(iChannel0, backgroundUv);
          float glassMix = clamp((0.88 + edgeZone * 0.10) * effectStrength, 0.22, 0.98);
          vec3 glassColor = mix(base.rgb, blurred.rgb, glassMix);
          glassColor = mix(glassColor, vec3(0.86, 0.95, 1.0), 0.07 * effectStrength);

          float rim = 1.0 - smoothstep(0.65, 3.2, edgeDistance);
          vec2 lightDirection = normalize(vec2(-0.65, -1.0));
          float lightFacing = 0.28 + 0.72 * max(dot(outwardNormal, lightDirection), 0.0);
          glassColor += vec3(rim * lightFacing * 0.34 * effectStrength);

          fragColor = vec4(clamp(glassColor, 0.0, 1.0), 1.0);
        }

        void main() {
          mainImage(gl_FragColor, gl_FragCoord.xy);
        }
      `;

      const vertexShader = this.compileShader(gl, gl.VERTEX_SHADER, vertexSource);
      const fragmentShader = this.compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
      if (!vertexShader || !fragmentShader) return false;

      const program = gl.createProgram();
      gl.attachShader(program, vertexShader);
      gl.attachShader(program, fragmentShader);
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return false;

      const buffer = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.bufferData(
        gl.ARRAY_BUFFER,
        new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
        gl.STATIC_DRAW
      );

      gl.useProgram(program);
      const position = gl.getAttribLocation(program, 'position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

      this.gl = gl;
      this.program = program;
      this.vertexShader = vertexShader;
      this.fragmentShader = fragmentShader;
      this.buffer = buffer;
      this.texture = gl.createTexture();
      this.uniforms = {
        resolution: gl.getUniformLocation(program, 'iResolution'),
        cardRect: gl.getUniformLocation(program, 'uCardRect'),
        textureRect: gl.getUniformLocation(program, 'uTextureRect'),
        texture: gl.getUniformLocation(program, 'iChannel0'),
        strength: gl.getUniformLocation(program, 'uStrength'),
      };

      return true;
    }

    compileShader(gl, type, source) {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    resize() {
      if (!this.gl) return;
      const bounds = this.root.getBoundingClientRect();
      const renderScale = bounds.width > 500 ? 0.75 : 0.9;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 1) * renderScale;
      const width = Math.max(1, Math.round(bounds.width * pixelRatio));
      const height = Math.max(1, Math.round(bounds.height * pixelRatio));

      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width;
        this.canvas.height = height;
      }
    }

    onResize() {
      this.resize();
      this.scheduleRender();
    }

    scheduleRender() {
      if (!this.gl || this.frame !== null) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.render();
      });
    }

    render() {
      const gl = this.gl;
      const cardRect = this.root.getBoundingClientRect();
      const textureRect = this.parallax.getBoundingClientRect();
      if (!gl || !this.canvas.width || !this.canvas.height || !textureRect.width || !textureRect.height) return;

      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.useProgram(this.program);
      gl.uniform2f(this.uniforms.resolution, this.canvas.width, this.canvas.height);
      gl.uniform4f(this.uniforms.cardRect, cardRect.left, cardRect.top, cardRect.width, cardRect.height);
      gl.uniform4f(
        this.uniforms.textureRect,
        textureRect.left,
        textureRect.top,
        textureRect.width,
        textureRect.height
      );
      gl.uniform1f(this.uniforms.strength, this.strength);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
      gl.uniform1i(this.uniforms.texture, 0);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      this.root.classList.add('is-glass-ready');
    }

    releaseContext() {
      const gl = this.gl;
      if (!gl) return;
      if (this.texture) gl.deleteTexture(this.texture);
      if (this.buffer) gl.deleteBuffer(this.buffer);
      if (this.program) gl.deleteProgram(this.program);
      if (this.vertexShader) gl.deleteShader(this.vertexShader);
      if (this.fragmentShader) gl.deleteShader(this.fragmentShader);
      this.gl = null;
    }

    destroy() {
      this.destroyed = true;
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.source?.removeEventListener('load', this.onSourceLoad);
      window.removeEventListener('scroll', this.scheduleRender);
      window.removeEventListener('resize', this.onResize);
      this.resizeObserver?.disconnect();
      this.releaseContext();
    }
  }

  class DevelopmentLiquidGlassV2Controller {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-development]');
      this.frameCoordinator = this.section?.developmentFrameCoordinator;
      this.parallax = this.section?.querySelector('[data-development-parallax]');
      this.background = root.querySelector('[data-development-liquidglass-v2-background]');
      this.backgroundContext = this.background?.getContext('2d');
      this.sourceSelector = root.dataset.developmentLiquidglassV2Source || '.development__texture--1';
      this.sources = root.hasAttribute('data-development-liquidglass-v2-composite-source')
        ? [...(this.section?.querySelectorAll('[data-development-texture]') || [])]
        : [this.section?.querySelector(this.sourceSelector)].filter(Boolean);
      this.source = this.sources[0];
      this.allCards = [...root.querySelectorAll('[data-development-liquidglass-v2-card]')];
      this.cards = [];
      this.moduleUrl = this.section?.dataset.developmentLiquidglassV2Url;
      this.mobileBreakpoint = window.matchMedia('(max-width: 749px)');
      this.desktopOnly = root.hasAttribute('data-development-liquidglass-v2-desktop-only');
      this.instance = null;
      this.lastGeometry = '';
      this.isVisible = this.isInViewport();
      this.destroyed = false;
      this.activationId = 0;
      this.usesSectionViewport = false;

      this.onResize = this.onResize.bind(this);
      this.onIntersection = this.onIntersection.bind(this);
      this.onMobileBreakpointChange = this.onMobileBreakpointChange.bind(this);
      this.syncBackground = this.syncBackground.bind(this);
    }

    init() {
      if (this.mobileBreakpoint.addEventListener) {
        this.mobileBreakpoint.addEventListener('change', this.onMobileBreakpointChange);
      } else {
        this.mobileBreakpoint.addListener?.(this.onMobileBreakpointChange);
      }
      this.activate();
    }

    getActiveCards() {
      const isMobile = this.mobileBreakpoint.matches;
      if (isMobile && this.desktopOnly) return [];

      return this.allCards.filter((card) => {
        const deviceSetting = isMobile
          ? card.dataset.developmentLiquidglassV2MobileEnabled
          : card.dataset.developmentLiquidglassV2DesktopEnabled;

        if (typeof deviceSetting !== 'undefined') return deviceSetting === 'true';
        if (!isMobile) return true;

        const mobileMode = card.dataset.developmentChoiceMobileMode || 'inherit';
        return mobileMode === 'inherit';
      });
    }

    async activate() {
      const activationId = ++this.activationId;
      this.cards = this.getActiveCards();
      this.root.dataset.developmentLiquidglassV2ActiveCards = String(this.cards.length);

      if (this.cards.length === 0) {
        this.resetBackgroundCanvas();
        return;
      }

      if (
        !this.background ||
        !this.backgroundContext ||
        !this.source ||
        !this.parallax ||
        !this.frameCoordinator ||
        !this.moduleUrl
      ) return;

      this.root.classList.add('is-liquidglass-v2-initializing');
      this.cards.forEach((card) => {
        const strength = clamp(Number(card.dataset.developmentGlassStrength || 65) / 100);
        card.dataset.config = JSON.stringify({
          blurAmount: 0.25,
          cornerRadius: 24,
          shadowOpacity: 0,
          shadowSpread: 0,
          shadowOffsetY: 0,

          // blurAmount: mix(0.18, 0.46, strength),
          // refraction: mix(0.64, 0.92, strength),
          // chromAberration: mix(0.025, 0.09, strength),
          // edgeHighlight: mix(0.12, 0.36, strength),
          // specular: mix(0.08, 0.32, strength),
          // fresnel: 1,
          // distortion: mix(0.01, 0.07, strength),
          // cornerRadius: 32,
          // zRadius: mix(20, 42, strength),
          // opacity: 1,
          // saturation: 0.04,
          // tintStrength: 0.02,
          // brightness: 0.01,
          // shadowOpacity: 0,
          // shadowSpread: 0,
          // shadowOffsetY: 0,
          // floating: false,
          // button: false,
          // bevelMode: 0,
        });
      });

      this.usesSectionViewport = Boolean(this.section?.developmentScrollController?.initialized);

      if (this.usesSectionViewport) {
        this.section.addEventListener('development:viewportchange', this.onResize);
      } else {
        window.addEventListener('resize', this.onResize, { passive: true });
        window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });
      }

      this.frameCoordinator.add(this.syncBackground);

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.onIntersection);
        this.intersectionObserver.observe(this.root);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.root);
        this.resizeObserver.observe(this.parallax);
      }

      this.syncBackground(performance.now(), true);

      try {
        const sourceReady = Promise.all(this.sources.map((source) => (
          source.complete && source.naturalWidth > 0
            ? Promise.resolve()
            : source.decode()
        )));
        await Promise.all([sourceReady, document.fonts?.ready]);
        if (this.destroyed || activationId !== this.activationId) return;

        this.lastGeometry = '';
        this.syncBackground(performance.now(), true);

        const { LiquidGlass } = await import(this.moduleUrl);
        if (this.destroyed || activationId !== this.activationId) return;

        const instance = await LiquidGlass.init({
          root: this.root,
          glassElements: this.cards,
          pixelRatio: LIQUID_GLASS_CONFIG.maxDevicePixelRatio,
        });

        if (this.destroyed || activationId !== this.activationId) {
          instance.destroy();
          return;
        }

        this.instance = instance;
        this.cards.forEach((card) => card.classList.add('is-liquidglass-v2-ready'));
        this.root.classList.remove('is-liquidglass-v2-initializing');
        this.instance.markChanged(this.background);
        this.instance.renderNow?.();
      } catch (error) {
        this.root.classList.remove('is-liquidglass-v2-initializing');
        this.root.classList.add('is-liquidglass-v2-fallback');
        console.warn('Development Liquid Glass V2 could not initialize.', error);
      }
    }

    onMobileBreakpointChange() {
      this.deactivate();
      this.activate();
    }

    onResize() {
      this.lastGeometry = '';
      this.frameCoordinator.request();
    }

    onIntersection(entries) {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      if (isVisible === this.isVisible) return;

      this.isVisible = isVisible;
      if (isVisible) {
        this.lastGeometry = '';
        this.frameCoordinator.request();
      }
    }

    isInViewport() {
      const rect = this.root.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    }

    resetBackgroundCanvas() {
      if (!this.background) return;
      this.background.width = 1;
      this.background.height = 1;
    }

    syncBackground(_timestamp, force = false) {
      if (this.destroyed || (!force && !this.isVisible)) return;

      const rootRect = this.root.getBoundingClientRect();
      const textureRect = this.parallax.getBoundingClientRect();
      const sourceOpacities = this.sources.map((source) => (
        clamp(Number(window.getComputedStyle(source).opacity || 1))
      ));
      if (
        this.sources.some((source) => !source.complete || !source.naturalWidth) ||
        !rootRect.width ||
        !rootRect.height ||
        !textureRect.width ||
        !textureRect.height
      ) return;

      const x = textureRect.left - rootRect.left;
      const y = textureRect.top - rootRect.top;
      const pixelRatio = Math.min(
        window.devicePixelRatio || 1,
        LIQUID_GLASS_CONFIG.maxDevicePixelRatio
      );
      const geometry = [
        x,
        y,
        textureRect.width,
        textureRect.height,
        rootRect.width,
        rootRect.height,
        pixelRatio,
        ...sourceOpacities,
      ]
        .map((value) => value.toFixed(2))
        .join(':');
      if (geometry === this.lastGeometry) return;

      this.lastGeometry = geometry;
      this.background.width = Math.max(1, Math.round(rootRect.width * pixelRatio));
      this.background.height = Math.max(1, Math.round(rootRect.height * pixelRatio));
      this.backgroundContext.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      this.backgroundContext.clearRect(0, 0, rootRect.width, rootRect.height);
      this.sources.forEach((source, index) => {
        this.backgroundContext.globalAlpha = sourceOpacities[index];
        this.backgroundContext.drawImage(
          source,
          x,
          y,
          textureRect.width,
          textureRect.height
        );
      });
      this.backgroundContext.globalAlpha = 1;
      this.instance?.markChanged(this.background);
      this.instance?.renderNow?.();
    }

    deactivate() {
      this.activationId += 1;

      if (this.usesSectionViewport) {
        this.section?.removeEventListener('development:viewportchange', this.onResize);
      } else {
        window.removeEventListener('resize', this.onResize);
        window.visualViewport?.removeEventListener('resize', this.onResize);
      }

      this.usesSectionViewport = false;
      this.frameCoordinator?.remove(this.syncBackground);
      this.intersectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.instance?.destroy();
      this.instance = null;
      this.allCards.forEach((card) => card.classList.remove('is-liquidglass-v2-ready'));
      this.root.classList.remove('is-liquidglass-v2-initializing', 'is-liquidglass-v2-fallback');
      this.resetBackgroundCanvas();
      this.lastGeometry = '';
      this.cards = [];
    }

    destroy() {
      this.destroyed = true;
      if (this.mobileBreakpoint.removeEventListener) {
        this.mobileBreakpoint.removeEventListener('change', this.onMobileBreakpointChange);
      } else {
        this.mobileBreakpoint.removeListener?.(this.onMobileBreakpointChange);
      }
      this.deactivate();
    }
  }

  class DevelopmentChoiceCardMotionController {
    constructor(root) {
      this.root = root;
      this.section = root.closest('[data-development]');
      this.frameCoordinator = this.section?.developmentFrameCoordinator;
      this.desktopMode = root.dataset.developmentChoiceCardMotionMode || 'static';
      this.mobileMode = root.dataset.developmentChoiceCardMobileMotion || 'inherit';
      this.mobileBreakpoint = window.matchMedia('(max-width: 749px)');
      this.mode = this.getEffectiveMode();
      this.startLine = clamp(
        Number(root.dataset.developmentChoiceCardMotionStartLine || 65) / 100,
        0.05,
        0.99
      );
      this.triggerLine = clamp(
        Number(root.dataset.developmentChoiceCardMotionTriggerLine || 20) / 100,
        0.05,
        0.95
      );
      this.duration = clamp(
        Number(root.dataset.developmentChoiceCardMotionDuration || 1200),
        300,
        5000
      );
      this.productDistance = clamp(
        Number(root.dataset.developmentChoiceCardProductDistance || 65),
        0,
        140
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.startedAt = null;
      this.destroyed = false;

      this.runFrame = this.runFrame.bind(this);
      this.onMobileBreakpointChange = this.onMobileBreakpointChange.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    }

    init() {
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      if (this.mobileBreakpoint.addEventListener) {
        this.mobileBreakpoint.addEventListener('change', this.onMobileBreakpointChange);
      } else {
        this.mobileBreakpoint.addListener?.(this.onMobileBreakpointChange);
      }
      this.activate();
    }

    getEffectiveMode() {
      const mode = this.mobileBreakpoint.matches && this.mobileMode !== 'inherit'
        ? this.mobileMode
        : this.desktopMode;

      return ['static', 'reveal-sequence', 'scroll-sequence'].includes(mode) ? mode : 'static';
    }

    syncModeClasses() {
      this.root.classList.remove(
        'development__choice-card-motion--static',
        'development__choice-card-motion--reveal-sequence',
        'development__choice-card-motion--scroll-sequence'
      );
      this.root.classList.add(`development__choice-card-motion--${this.mode}`);
      this.root.dataset.developmentChoiceCardMotionEffective = this.mode;
    }

    activate() {
      this.mode = this.getEffectiveMode();
      this.syncModeClasses();
      this.frameCoordinator?.remove(this.runFrame);
      this.root.classList.remove('is-card-motion-active', 'is-card-motion-complete');
      this.startedAt = null;

      if (
        this.reducedMotion.matches ||
        this.mode === 'static' ||
        !this.frameCoordinator
      ) {
        this.applyProgress(1);
        this.root.classList.add('is-card-motion-complete');
        return;
      }

      this.applyProgress(0);
      this.frameCoordinator.add(this.runFrame);
      this.frameCoordinator.request();
    }

    runFrame(timestamp) {
      if (this.destroyed) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight;

      if (this.mode === 'reveal-sequence') {
        if (this.startedAt === null) {
          const cardTop = this.frameCoordinator?.getRect(this.root)?.top ??
            this.root.getBoundingClientRect().top;
          if (cardTop > viewportHeight * this.startLine) return;
          this.startedAt = timestamp;
          this.root.classList.add('is-card-motion-active');
        }

        const progress = clamp((timestamp - this.startedAt) / this.duration);
        this.applyProgress(progress);

        if (progress >= 1) {
          this.root.classList.remove('is-card-motion-active');
          this.root.classList.add('is-card-motion-complete');
          this.frameCoordinator.remove(this.runFrame);
          return;
        }

        this.frameCoordinator.request();
        return;
      }

      const cardTop = this.frameCoordinator?.getRect(this.root)?.top ??
        this.root.getBoundingClientRect().top;
      const startY = viewportHeight * this.startLine;
      const endY = viewportHeight * this.triggerLine;
      const progress = clamp((startY - cardTop) / Math.max(startY - endY, 1));

      this.applyProgress(progress);
      this.root.classList.toggle('is-card-motion-active', progress > 0 && progress < 1);
      this.root.classList.toggle('is-card-motion-complete', progress >= 0.999);
    }

    applyProgress(progress) {
      const normalizedProgress = clamp(progress);
      const brandProgress = easeInOutCubic(progressBetween(normalizedProgress, 0.08, 0.5));
      const productProgress = easeOutCubic(progressBetween(normalizedProgress, 0.34, 0.88));
      const brandTop = mix(50, 23, brandProgress);
      const productY = mix(this.productDistance, 0, productProgress);

      this.root.style.setProperty(
        '--development-choice-card-brand-top',
        `${brandTop.toFixed(2)}%`
      );
      this.root.style.setProperty(
        '--development-choice-card-product-y',
        `${productY.toFixed(2)}%`
      );
      this.root.style.setProperty(
        '--development-choice-card-product-opacity',
        productProgress.toFixed(3)
      );
    }

    onMotionPreferenceChange() {
      this.activate();
    }

    onMobileBreakpointChange() {
      this.activate();
    }

    destroy() {
      this.destroyed = true;
      this.frameCoordinator?.remove(this.runFrame);
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      if (this.mobileBreakpoint.removeEventListener) {
        this.mobileBreakpoint.removeEventListener('change', this.onMobileBreakpointChange);
      } else {
        this.mobileBreakpoint.removeListener?.(this.onMobileBreakpointChange);
      }
    }
  }

  class DevelopmentChoiceButtonMotionController {
    constructor(root) {
      this.root = root;
      this.card = root.closest('[data-development-choice-card]');
      this.section = root.closest('[data-development]');
      this.frameCoordinator = this.section?.developmentFrameCoordinator;
      this.mode = root.dataset.developmentChoiceButtonMotionMode || 'static';
      this.triggerLine = clamp(
        Number(root.dataset.developmentChoiceButtonTriggerLine || 60) / 100,
        0.5,
        0.95
      );
      this.distance = clamp(
        Number(root.dataset.developmentChoiceButtonDistance || 24),
        0,
        96
      );
      this.duration = clamp(
        Number(root.dataset.developmentChoiceButtonDuration || 650),
        0,
        5000
      );
      this.glowStrength = clamp(
        Number(root.dataset.developmentChoiceButtonGlowStrength || 100) / 100
      );
      this.superGlowEnabled = root.dataset.developmentChoiceButtonSuperGlow === 'true';
      this.superGlowStrength = clamp(
        Number(root.dataset.developmentChoiceButtonSuperGlowStrength || 180) / 100,
        0,
        2.5
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.revealed = false;
      this.destroyed = false;

      this.runFrame = this.runFrame.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
    }

    init() {
      if (!this.card) return;

      this.root.style.setProperty('--development-choice-button-duration', `${this.duration}ms`);
      this.root.style.setProperty('--development-choice-button-distance', `${this.distance}px`);
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      this.activate();
    }

    activate() {
      this.frameCoordinator?.remove(this.runFrame);
      this.root.classList.remove('is-revealed', 'is-motion-interactive');
      this.revealed = false;
      this.applySuperGlow(0);

      if (
        this.reducedMotion.matches ||
        !this.frameCoordinator
      ) {
        this.applyProgress(1);
        this.root.classList.add('is-revealed', 'is-motion-interactive');
        return;
      }

      if (this.mode === 'static') {
        this.applyProgress(1);
        this.root.classList.add('is-revealed', 'is-motion-interactive');

        if (this.superGlowEnabled) {
          this.frameCoordinator.add(this.runFrame);
          this.frameCoordinator.request();
        }
        return;
      }

      this.applyProgress(0);
      this.frameCoordinator.add(this.runFrame);
      this.frameCoordinator.request();
    }

    runFrame() {
      if (this.destroyed || !this.card) return;

      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const cardTop = this.frameCoordinator?.getRect(this.card)?.top ??
        this.card.getBoundingClientRect().top;
      const buttonTop = cardTop + this.root.offsetTop;
      const superGlowProgress = this.getSuperGlowProgress(buttonTop, viewportHeight);

      if (this.mode === 'static') {
        this.applySuperGlow(superGlowProgress);
        return;
      }

      if (this.mode === 'reveal-up' || this.mode === 'reveal-down') {
        if (buttonTop <= viewportHeight * this.triggerLine) this.reveal();
        if (this.revealed) this.applySuperGlow(superGlowProgress);
        return;
      }

      const startY = viewportHeight * 0.98;
      const endY = viewportHeight * this.triggerLine;
      const progress = clamp((startY - buttonTop) / Math.max(startY - endY, 1));

      this.applyProgress(progress);
      this.applySuperGlow(superGlowProgress);
      this.root.classList.toggle('is-motion-interactive', progress > 0.08);
      this.root.classList.toggle('is-revealed', progress >= 0.999);
    }

    reveal() {
      if (this.revealed) return;

      this.revealed = true;
      this.applyProgress(1);
      this.root.classList.add('is-revealed', 'is-motion-interactive');
      if (!this.superGlowEnabled) this.frameCoordinator?.remove(this.runFrame);
    }

    applyProgress(progress) {
      const normalizedProgress = clamp(progress);
      const direction = this.mode.endsWith('down') ? -1 : 1;
      const translateY = direction * this.distance * (1 - normalizedProgress);
      const glowProgress = normalizedProgress;
      const shimmerProgress = progressBetween(glowProgress, 0.18, 0.8) * this.glowStrength;

      this.root.style.setProperty(
        '--development-choice-button-opacity',
        normalizedProgress.toFixed(3)
      );
      this.root.style.setProperty(
        '--development-choice-button-y',
        `${translateY.toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-glow-soft-blur',
        `${mix(4, 16, glowProgress).toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-glow-soft-opacity',
        (mix(0.12, 0.78, glowProgress) * this.glowStrength).toFixed(3)
      );
      this.root.style.setProperty(
        '--development-choice-button-glow-wide-blur',
        `${mix(10, 36, glowProgress).toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-glow-wide-opacity',
        (mix(0.08, 0.58, glowProgress) * this.glowStrength).toFixed(3)
      );
      this.root.style.setProperty(
        '--development-choice-button-shimmer-opacity',
        shimmerProgress.toFixed(3)
      );
    }

    getSuperGlowProgress(buttonTop, viewportHeight) {
      if (!this.superGlowEnabled || this.reducedMotion.matches) return 0;

      const startY = viewportHeight * this.triggerLine;
      const endY = -this.root.offsetHeight;
      return clamp((startY - buttonTop) / Math.max(startY - endY, 1));
    }

    applySuperGlow(progress) {
      const normalizedProgress = this.superGlowEnabled && !this.reducedMotion.matches
        ? easeInOutCubic(clamp(progress))
        : 0;
      const opacityStrength = Math.min(this.superGlowStrength, 2.2);

      this.root.style.setProperty(
        '--development-choice-button-super-glow-core-blur',
        `${(32 * this.superGlowStrength * normalizedProgress).toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-super-glow-core-spread',
        `${(3.5 * this.superGlowStrength * normalizedProgress).toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-super-glow-core-opacity',
        Math.min(0.42 * opacityStrength * normalizedProgress, 0.94).toFixed(3)
      );
      this.root.style.setProperty(
        '--development-choice-button-super-glow-wide-blur',
        `${(72 * this.superGlowStrength * normalizedProgress).toFixed(2)}px`
      );
      this.root.style.setProperty(
        '--development-choice-button-super-glow-wide-opacity',
        Math.min(0.32 * opacityStrength * normalizedProgress, 0.72).toFixed(3)
      );
    }

    onMotionPreferenceChange() {
      this.activate();
    }

    destroy() {
      this.destroyed = true;
      this.frameCoordinator?.remove(this.runFrame);
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
    }
  }

  class DevelopmentInsoleMapController {
    constructor(root) {
      this.root = root;
      this.hotspots = [...root.querySelectorAll('[data-development-insole-hotspot]')];
      this.details = root.querySelector('[data-development-insole-details]');
      this.detailsImage = root.querySelector('[data-development-insole-details-image]');
      this.detailsTitle = root.querySelector('[data-development-insole-details-title]');
      this.detailsDescription = root.querySelector('[data-development-insole-details-description]');
      this.closeButton = root.querySelector('[data-development-insole-details-close]');
      this.closeMark = root.querySelector('[data-development-insole-details-close-mark]');
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.activeHotspot = null;
      this.isModal = false;
      this.restoreFocusOnClose = false;
      this.detailsImageRequestId = 0;

      this.onClick = this.onClick.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogCancel = this.onDialogCancel.bind(this);
      this.onDialogClose = this.onDialogClose.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onResize = this.onResize.bind(this);
    }

    init() {
      if (this.hotspots.length === 0 || !this.details) return;

      this.root.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      this.details.addEventListener('click', this.onDialogClick);
      this.details.addEventListener('cancel', this.onDialogCancel);
      this.details.addEventListener('close', this.onDialogClose);
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.mobileMedia.addEventListener?.('change', this.onBreakpointChange);
      window.addEventListener('resize', this.onResize);
    }

    onClick(event) {
      const hotspot = event.target.closest('[data-development-insole-hotspot]');
      if (!hotspot || !this.root.contains(hotspot)) return;

      event.preventDefault();

      if (this.activeHotspot === hotspot && this.details.open) {
        this.closeDetails();
        return;
      }

      this.openDetails(hotspot);
    }

    onDocumentClick(event) {
      if (!this.details.open) return;
      if (event.target.closest('[data-development-insole-hotspot]')) return;
      if (this.details.contains(event.target)) return;

      this.closeDetails(false);
    }

    onKeydown(event) {
      if (event.key !== 'Escape' || !this.details.open) return;

      event.preventDefault();
      this.closeDetails();
    }

    onDialogClick(event) {
      if (!this.isModal || event.target !== this.details) return;

      const rect = this.details.getBoundingClientRect();
      const clickedOutside = event.clientX < rect.left
        || event.clientX > rect.right
        || event.clientY < rect.top
        || event.clientY > rect.bottom;

      if (clickedOutside) this.closeDetails();
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.closeDetails();
    }

    onDialogClose() {
      const hotspot = this.activeHotspot;

      this.hotspots.forEach((hotspot) => {
        hotspot.setAttribute('aria-expanded', 'false');
      });

      this.activeHotspot = null;
      this.isModal = false;
      this.details.removeAttribute('aria-modal');
      this.details.removeAttribute('data-development-insole-details-side');
      this.details.style.removeProperty('left');
      this.details.style.removeProperty('top');
      this.details.style.removeProperty('width');
      this.closeButton?.style.removeProperty('top');
      this.closeMark?.style.removeProperty('transform');
      document.documentElement.classList.remove('development-insole-dialog-open');

      if (this.restoreFocusOnClose && !this.mobileMedia.matches) {
        hotspot?.focus({ preventScroll: true });
      } else {
        hotspot?.blur();
      }
      this.restoreFocusOnClose = false;
    }

    onCloseClick() {
      this.closeDetails();
    }

    onBreakpointChange() {
      if (!this.activeHotspot || !this.details.open) return;

      const hotspot = this.activeHotspot;
      this.closeDetails(false);
      window.requestAnimationFrame(() => this.openDetails(hotspot));
    }

    onResize() {
      if (!this.details.open) return;

      if (!this.mobileMedia.matches) this.positionDetails();
      this.positionCloseButton();
    }

    openDetails(hotspot) {
      const shouldUseModal = this.mobileMedia.matches;

      if (this.details.open && this.isModal !== shouldUseModal) {
        this.closeDetails(false);
      }

      this.hotspots.forEach((item) => {
        item.setAttribute('aria-expanded', String(item === hotspot));
      });

      this.activeHotspot = hotspot;
      this.updateDetailsImage(hotspot);
      this.detailsTitle.textContent = hotspot.dataset.developmentInsoleTitle || '';
      this.detailsDescription.textContent = hotspot.dataset.developmentInsoleDescription || '';

      if (!this.details.open) {
        if (shouldUseModal) {
          this.details.setAttribute('aria-modal', 'true');
          this.details.showModal();
          hotspot.blur();
        } else {
          this.details.removeAttribute('aria-modal');
          this.openInlineDetails();
          hotspot.blur();
        }
      }

      this.isModal = shouldUseModal;
      document.documentElement.classList.toggle('development-insole-dialog-open', shouldUseModal);

      if (!shouldUseModal) {
        window.requestAnimationFrame(() => {
          this.positionDetails();
          this.positionCloseButton();
        });
      } else {
        this.positionCloseButton();
      }
    }

    updateDetailsImage(hotspot) {
      if (!this.detailsImage) return;

      const imageUrl = hotspot.dataset.developmentInsoleImage || '';
      const requestId = ++this.detailsImageRequestId;

      if (!imageUrl) {
        this.detailsImage.onload = null;
        this.detailsImage.onerror = null;
        this.detailsImage.removeAttribute('src');
        this.detailsImage.style.removeProperty('opacity');
        this.detailsImage.hidden = true;
        return;
      }

      const revealCurrentImage = () => {
        if (requestId === this.detailsImageRequestId) {
          this.detailsImage.style.opacity = '1';
        }
      };

      const showLoadedImage = () => {
        if (requestId !== this.detailsImageRequestId) return;

        this.detailsImage.onload = null;
        this.detailsImage.onerror = null;
        this.detailsImage.src = imageUrl;

        if (typeof this.detailsImage.decode === 'function') {
          this.detailsImage.decode().then(revealCurrentImage).catch(() => {});
        } else {
          this.detailsImage.onload = revealCurrentImage;
          if (this.detailsImage.complete && this.detailsImage.naturalWidth > 0) {
            revealCurrentImage();
          }
        }
      };

      this.detailsImage.style.opacity = '0';
      this.detailsImage.width = Number(hotspot.dataset.developmentInsoleImageWidth) || 802;
      this.detailsImage.height = Number(hotspot.dataset.developmentInsoleImageHeight) || 656;
      this.detailsImage.hidden = false;

      const preload = new Image();

      if (typeof preload.decode === 'function') {
        preload.src = imageUrl;
        preload.decode().then(showLoadedImage).catch(() => {});
      } else {
        preload.onload = showLoadedImage;
        preload.src = imageUrl;
      }
    }

    positionCloseButton() {
      if (!this.closeButton || !this.closeMark || !this.root.classList.contains('development-insole-map--image-details')) {
        return;
      }

      const widthScale = this.details.offsetWidth / 401;
      const scale = Math.max(40 / 48, Math.min(1, widthScale || 1));

      this.closeButton.style.top = `${(-24 - 43 * scale).toFixed(2)}px`;
      this.closeMark.style.transform = `scale(${scale.toFixed(3)})`;
    }

    openInlineDetails() {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      this.details.setAttribute('open', '');
      this.details.blur();

      if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
        window.scrollTo(scrollX, scrollY);
      }
    }

    positionDetails() {
      if (!this.activeHotspot || !this.details.open) return;

      this.details.style.removeProperty('width');

      const mapRect = this.root.getBoundingClientRect();
      const hotspotRect = this.activeHotspot.getBoundingClientRect();
      let panelWidth = this.details.offsetWidth;
      const panelHeight = this.details.offsetHeight;
      const hotspotX = hotspotRect.left - mapRect.left + hotspotRect.width / 2;
      const hotspotY = hotspotRect.top - mapRect.top + hotspotRect.height / 2;
      const gap = 18;
      const edge = 16;
      const rightPosition = hotspotX + hotspotRect.width / 2 + gap;
      let leftPosition = hotspotX - hotspotRect.width / 2 - gap - panelWidth;
      const requestedSide = this.activeHotspot.dataset.developmentInsoleDetailsSide || 'auto';
      const availableRight = window.innerWidth - edge - (mapRect.left + rightPosition);
      const availableLeft = mapRect.left + hotspotX - hotspotRect.width / 2 - gap - edge;
      const resolvedSide = requestedSide === 'left' || requestedSide === 'right'
        ? requestedSide
        : availableRight >= panelWidth || availableRight >= availableLeft
          ? 'right'
          : 'left';
      const availableWidth = resolvedSide === 'right' ? availableRight : availableLeft;

      if (availableWidth < panelWidth) {
        panelWidth = Math.max(0, availableWidth);
        this.details.style.width = `${panelWidth.toFixed(2)}px`;
        leftPosition = hotspotX - hotspotRect.width / 2 - gap - panelWidth;
      }

      const preferredLeft = resolvedSide === 'right' ? rightPosition : leftPosition;
      const viewportMinLeft = edge - mapRect.left;
      const viewportMaxLeft = window.innerWidth - edge - panelWidth - mapRect.left;
      const left = viewportMaxLeft >= viewportMinLeft
        ? clamp(preferredLeft, viewportMinLeft, viewportMaxLeft)
        : preferredLeft;
      const maxTop = Math.max(edge, mapRect.height - panelHeight - edge);
      const top = clamp(hotspotY - panelHeight / 2, edge, maxTop);

      this.details.dataset.developmentInsoleDetailsSide = resolvedSide;
      this.details.style.left = `${left.toFixed(2)}px`;
      this.details.style.top = `${top.toFixed(2)}px`;
    }

    closeDetails(restoreFocus = false) {
      const details = this.details;
      if (!details?.open) return;

      this.restoreFocusOnClose = restoreFocus;
      details.close();
    }

    destroy() {
      this.closeDetails(false);
      this.root.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      this.details?.removeEventListener('click', this.onDialogClick);
      this.details?.removeEventListener('cancel', this.onDialogCancel);
      this.details?.removeEventListener('close', this.onDialogClose);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.mobileMedia.removeEventListener?.('change', this.onBreakpointChange);
      window.removeEventListener('resize', this.onResize);
    }
  }

  class DevelopmentStepsController {
    constructor(root) {
      this.root = root;
      this.tabs = [...root.querySelectorAll('[data-development-step-tab]')];
      this.panels = [...root.querySelectorAll('[data-development-step-panel]')];
      this.tablist = root.querySelector('.development-steps__tablist');
      this.mode = root.dataset.developmentStepsMode || 'tabs';
      this.desktopMedia = window.matchMedia('(min-width: 750px)');
      this.isDesktopScroll = false;
      this.isMobileStack = false;
      this.activeIndex = 0;
      this.visiblePanels = new Map();
      this.observer = null;

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onIntersection = this.onIntersection.bind(this);
    }

    init() {
      if (this.tabs.length === 0 || this.panels.length === 0) return;

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onClick);
        tab.addEventListener('keydown', this.onKeyDown);
      });

      const selectedIndex = this.tabs.findIndex(
        (tab) => tab.getAttribute('aria-selected') === 'true' || tab.getAttribute('aria-current') === 'true'
      );
      this.activeIndex = selectedIndex >= 0 ? selectedIndex : 0;
      this.desktopMedia.addEventListener?.('change', this.onBreakpointChange);
      this.setLayout();
    }

    onClick(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      if (this.isDesktopScroll) {
        this.scrollToPanel(index);
        return;
      }

      this.activate(index);
    }

    onKeyDown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const enabledTabs = this.tabs.filter((tab) => !tab.disabled);
      const currentIndex = enabledTabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      event.preventDefault();
      let nextIndex;

      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = enabledTabs.length - 1;
      } else {
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        nextIndex = (currentIndex + direction + enabledTabs.length) % enabledTabs.length;
      }

      const nextTab = enabledTabs[nextIndex];
      const nextTabIndex = this.tabs.indexOf(nextTab);
      if (this.isDesktopScroll) {
        this.scrollToPanel(nextTabIndex, true);
        return;
      }

      this.activate(nextTabIndex, true);
    }

    activate(index, focus = false) {
      const activeTab = this.tabs[index];
      if (!activeTab || activeTab.disabled) return;

      this.activeIndex = index;
      const activeStep = activeTab.dataset.developmentStepTab;
      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);

        if (this.isDesktopScroll) {
          tab.removeAttribute('aria-selected');
          tab.setAttribute('aria-current', isActive ? 'true' : 'false');
          tab.tabIndex = 0;
        } else {
          tab.removeAttribute('aria-current');
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        }
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.developmentStepPanel === activeStep;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = this.isDesktopScroll || this.isMobileStack ? false : !isActive;
      });

      if (focus) activeTab.focus();
    }

    setLayout() {
      this.isDesktopScroll = this.mode === 'scroll' && this.desktopMedia.matches;
      this.isMobileStack = !this.desktopMedia.matches && this.root.classList.contains('development-steps--mobile-stack');
      if (this.tablist) this.tablist.hidden = this.isMobileStack;

      if (this.isMobileStack) {
        this.stopObserver();
        this.tabs.forEach((tab) => {
          tab.removeAttribute('role');
          tab.removeAttribute('aria-selected');
          tab.removeAttribute('aria-current');
          tab.tabIndex = -1;
        });
        this.panels.forEach((panel) => {
          panel.hidden = false;
          panel.setAttribute('role', 'region');
          panel.setAttribute('aria-labelledby', panel.querySelector('.development-steps__panel-title').id);
        });
        return;
      }

      this.panels.forEach((panel, index) => panel.setAttribute('aria-labelledby', this.tabs[index].id));

      if (this.isDesktopScroll) {
        this.tablist?.setAttribute('role', 'navigation');
        this.tabs.forEach((tab) => tab.removeAttribute('role'));
        this.panels.forEach((panel) => panel.setAttribute('role', 'region'));
        this.activate(this.activeIndex);
        this.startObserver();
        return;
      }

      this.stopObserver();
      this.tablist?.setAttribute('role', 'tablist');
      this.tabs.forEach((tab) => tab.setAttribute('role', 'tab'));
      this.panels.forEach((panel) => panel.setAttribute('role', 'tabpanel'));
      this.activate(this.activeIndex);
    }

    onBreakpointChange() {
      this.setLayout();
    }

    scrollToPanel(index, focus = false) {
      const panel = this.panels[index];
      if (!panel) return;

      this.activate(index, focus);
      const smootherController = this.root.closest('[data-development]')?.developmentScrollSmootherController;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (smootherController?.scrollTo(panel, !reducedMotion, 'center center')) return;

      panel.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }

    startObserver() {
      this.stopObserver();
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(this.onIntersection, {
        root: null,
        rootMargin: '-32% 0px -48% 0px',
        threshold: [0, 0.2, 0.5, 0.8, 1],
      });

      this.panels.forEach((panel) => this.observer.observe(panel));
    }

    onIntersection(entries) {
      entries.forEach((entry) => {
        const id = entry.target.dataset.developmentStepPanel;
        if (entry.isIntersecting) {
          this.visiblePanels.set(id, entry);
        } else {
          this.visiblePanels.delete(id);
        }
      });

      const candidates = [...this.visiblePanels.values()];
      if (!candidates.length) return;

      const marker = window.innerHeight * 0.38;
      candidates.sort(
        (a, b) =>
          Math.abs(a.boundingClientRect.top - marker) - Math.abs(b.boundingClientRect.top - marker)
      );

      const index = this.panels.indexOf(candidates[0].target);
      if (index >= 0) this.activate(index);
    }

    stopObserver() {
      this.observer?.disconnect();
      this.observer = null;
      this.visiblePanels.clear();
    }

    destroy() {
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onClick);
        tab.removeEventListener('keydown', this.onKeyDown);
      });
      this.desktopMedia.removeEventListener?.('change', this.onBreakpointChange);
      this.stopObserver();
    }
  }

  class DevelopmentModelsController {
    constructor(root) {
      this.root = root;
      this.tabs = [...root.querySelectorAll('[data-development-model-tab]')];
      this.panels = [...root.querySelectorAll('[data-development-model-panel]')];
      this.visuals = [...root.querySelectorAll('[data-development-model-visual]')];
      this.originalTabOrder = [...this.tabs];
      this.mobileMedia = window.matchMedia('(max-width: 749px)');
      this.hasMobileSequence = Boolean(root.closest('.development-steps--mobile-stack'));

      this.onClick = this.onClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.orderTabs = this.orderTabs.bind(this);
    }

    init() {
      if (this.tabs.length === 0 || this.panels.length === 0) return;

      if (this.hasMobileSequence) {
        this.orderTabs();
        this.mobileMedia.addEventListener?.('change', this.orderTabs);
      }

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', this.onClick);
        tab.addEventListener('keydown', this.onKeyDown);
      });

      const selectedIndex = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
      this.activate(selectedIndex >= 0 ? selectedIndex : 0);
    }

    orderTabs() {
      const mobileOrder = ['blade', 'carve', 'everyday', 'response', 'luxe'];
      this.tabs = [...this.originalTabOrder];
      if (this.mobileMedia.matches) {
        this.tabs.sort((a, b) => {
          const aIndex = mobileOrder.indexOf(a.dataset.developmentModelKey);
          const bIndex = mobileOrder.indexOf(b.dataset.developmentModelKey);
          return (aIndex < 0 ? mobileOrder.length : aIndex) - (bIndex < 0 ? mobileOrder.length : bIndex);
        });
      }
      this.tabs.forEach((tab) => tab.parentElement.append(tab));
    }

    onClick(event) {
      this.activate(this.tabs.indexOf(event.currentTarget));
    }

    onKeyDown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const currentIndex = this.tabs.indexOf(event.currentTarget);
      if (currentIndex < 0) return;

      event.preventDefault();
      let nextIndex;

      if (event.key === 'Home') {
        nextIndex = 0;
      } else if (event.key === 'End') {
        nextIndex = this.tabs.length - 1;
      } else {
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        nextIndex = (currentIndex + direction + this.tabs.length) % this.tabs.length;
      }

      this.activate(nextIndex, true);
    }

    activate(index, focus = false) {
      const activeTab = this.tabs[index];
      if (!activeTab) return;

      const activeModel = activeTab.dataset.developmentModelTab;
      this.tabs.forEach((tab) => {
        const isActive = tab === activeTab;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const isActive = panel.dataset.developmentModelPanel === activeModel;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.visuals.forEach((visual) => {
        const isActive = visual.dataset.developmentModelVisual === activeModel;
        visual.classList.toggle('is-active', isActive);
        visual.hidden = !isActive;
      });

      if (focus) activeTab.focus();
    }

    destroy() {
      this.mobileMedia.removeEventListener?.('change', this.orderTabs);
      this.tabs.forEach((tab) => {
        tab.removeEventListener('click', this.onClick);
        tab.removeEventListener('keydown', this.onKeyDown);
      });
    }
  }

  class DevelopmentHeroModelCarousel {
    constructor(root) {
      this.root = root;
      this.mediaItems = [...root.querySelectorAll('[data-hero-model-media]')];
      this.items = [...root.querySelectorAll('[data-model-item]')];
      this.prev = root.querySelector('[data-hero-model-prev]');
      this.next = root.querySelector('[data-hero-model-next]');
      this.total = Math.min(this.mediaItems.length, this.items.length);
      this.loadingStrategy = root.dataset.videoLoadingStrategy || 'staged';
      this.startupTimeout = clamp(
        Number(root.dataset.videoStartupTimeout || 5000),
        1500,
        15000
      );
      this.stallTimeout = clamp(
        Number(root.dataset.videoStallTimeout || 3000),
        1000,
        10000
      );
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileQuery = window.matchMedia(
        '(max-width: 749px), (max-height: 749px) and (pointer: coarse)'
      );
      this.activeIndex = -1;
      this.elapsed = 0;
      this.startedAt = null;
      this.frame = null;
      this.videoWatchdogTimer = null;
      this.videoWatchdogIndex = -1;
      this.transitionCleanupTimer = null;
      this.isVisible = !('IntersectionObserver' in window);
      this.videoHandlers = [];
      this.videoFallbackIndexes = new Set();
      this.managedResetTimers = new Map();

      this.tick = this.tick.bind(this);
      this.onItemClick = this.onItemClick.bind(this);
      this.onPrevious = this.onPrevious.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
      this.onIntersection = this.onIntersection.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
    }

    init() {
      if (this.total === 0) return;

      this.root.addEventListener('click', this.onItemClick);
      this.prev?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      this.mobileQuery.addEventListener?.('change', this.onBreakpointChange);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('pageshow', this.onPageShow);
      this.bindVideoEvents();

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
          threshold: 0.1,
        });
        this.intersectionObserver.observe(this.root);
      }

      this.setActiveModel(0);
    }

    bindVideoEvents() {
      this.mediaItems.forEach((media, index) => {
        const video = media.querySelector('[data-hero-model-video]');
        if (!video) return;

        const loadedmetadata = () => {
          this.setVideoState(index, index === this.activeIndex ? 'loading' : 'prepared');
        };
        const canplay = () => {
          this.setVideoState(index, index === this.activeIndex ? 'ready' : 'prepared');
        };
        const playing = () => {
          if (index !== this.activeIndex) return;
          this.clearVideoWatchdog();
          this.videoFallbackIndexes.delete(index);
          media.classList.add('is-video-playing');
          this.setVideoState(index, 'playing');
          this.startProgress(index);
          if (!this.mobileQuery.matches) this.prepareNextVideo(index);
        };
        const buffering = () => {
          if (index !== this.activeIndex || this.videoFallbackIndexes.has(index)) return;
          this.pauseProgress(index);
          this.setVideoState(index, 'buffering');
          this.armVideoWatchdog(index, 'stall');
        };
        const paused = () => {
          if (
            index !== this.activeIndex
            || video.ended
            || this.videoFallbackIndexes.has(index)
            || !this.shouldRun()
            || video.dataset.videoManagedReset
          ) return;

          this.pauseProgress(index);
          this.setVideoState(index, 'paused');
          this.armVideoWatchdog(index, 'startup');
        };
        const ended = () => {
          if (index === this.activeIndex && this.getDurationMode(index) === 'full_video') {
            this.clearVideoWatchdog();
            this.setActiveModel(index + 1);
          }
        };
        const error = () => {
          if (index === this.activeIndex) {
            this.useVideoFallback(index, 'error');
          } else {
            this.detachVideo(index);
          }
        };
        const abort = () => {
          if (video.dataset.videoManagedReset) return;
          if (index === this.activeIndex) {
            this.useVideoFallback(index, 'abort');
          } else {
            this.detachVideo(index);
          }
        };

        video.addEventListener('loadedmetadata', loadedmetadata);
        video.addEventListener('canplay', canplay);
        video.addEventListener('playing', playing);
        video.addEventListener('waiting', buffering);
        video.addEventListener('stalled', buffering);
        video.addEventListener('pause', paused);
        video.addEventListener('ended', ended);
        video.addEventListener('error', error);
        video.addEventListener('abort', abort);
        this.videoHandlers.push({
          video,
          loadedmetadata,
          canplay,
          playing,
          buffering,
          paused,
          ended,
          error,
          abort,
        });
      });
    }

    shouldRun() {
      return !this.reducedMotion.matches && this.isVisible && !document.hidden;
    }

    setVideoState(index, state) {
      const media = this.mediaItems[index];
      const video = this.getVideo(index);
      if (media) media.dataset.videoState = state;
      if (video) video.dataset.videoState = state;
      if (index === this.activeIndex) this.root.dataset.videoState = state;
    }

    getVideoSource(video) {
      if (!video) return '';
      return this.mobileQuery.matches
        ? video.dataset.videoSrcMobile || video.dataset.videoSrcDesktop || ''
        : video.dataset.videoSrcDesktop || video.dataset.videoSrcMobile || '';
    }

    markManagedReset(video) {
      const existingTimer = this.managedResetTimers.get(video);
      if (existingTimer) window.clearTimeout(existingTimer);

      const version = String(Number(video.dataset.videoManagedReset || 0) + 1);
      video.dataset.videoManagedReset = version;
      const timer = window.setTimeout(() => {
        if (video.dataset.videoManagedReset === version) {
          delete video.dataset.videoManagedReset;
        }
        this.managedResetTimers.delete(video);
      }, 250);

      this.managedResetTimers.set(video, timer);
    }

    attachVideo(index, preload = 'auto') {
      const video = this.getVideo(index);
      const source = this.getVideoSource(video);
      if (!video || !source) return null;

      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.preload = preload;

      if (video.dataset.videoAttachedSource !== source || !video.getAttribute('src')) {
        this.markManagedReset(video);
        video.pause();
        video.src = source;
        video.dataset.videoAttachedSource = source;
        this.setVideoState(index, 'loading');
        video.load();
      }

      return video;
    }

    detachVideo(index) {
      const video = this.getVideo(index);
      if (!video || (!video.getAttribute('src') && !video.dataset.videoAttachedSource)) return;

      this.markManagedReset(video);
      video.pause();
      video.removeAttribute('src');
      video.preload = 'none';
      video.load();
      delete video.dataset.videoAttachedSource;
      this.mediaItems[index]?.classList.remove('is-video-playing');
      this.setVideoState(index, 'detached');
    }

    releaseAllVideos() {
      this.mediaItems.forEach((media, index) => {
        if (media.querySelector('[data-hero-model-video]')) this.detachVideo(index);
      });
    }

    pruneVideoWindow(activeIndex, nextIndex = -1) {
      const retainedIndexes = new Set([activeIndex]);
      if (nextIndex >= 0) retainedIndexes.add(nextIndex);

      this.mediaItems.forEach((media, index) => {
        if (!retainedIndexes.has(index) && media.querySelector('[data-hero-model-video]')) {
          this.detachVideo(index);
        }
      });
    }

    getDurationMode(index) {
      const mode =
        this.items[index]?.dataset.modelDurationMode ||
        this.mediaItems[index]?.dataset.modelDurationMode;

      return mode === 'custom' ? 'custom' : 'full_video';
    }

    getConfiguredDuration(index) {
      const duration = Number(
        this.items[index]?.dataset.modelDuration ||
        this.mediaItems[index]?.dataset.modelDuration ||
        5000
      );

      return Number.isFinite(duration) && duration > 0 ? duration : 5000;
    }

    getVideo(index) {
      return this.mediaItems[index]?.querySelector('[data-hero-model-video]') || null;
    }

    getDuration(index) {
      if (this.getDurationMode(index) === 'full_video' && !this.videoFallbackIndexes.has(index)) {
        const videoDuration = Number(this.getVideo(index)?.duration) * 1000;
        if (Number.isFinite(videoDuration) && videoDuration > 0) return videoDuration;
      }

      return this.getConfiguredDuration(index);
    }

    renderProgress(progress) {
      this.items.forEach((item, index) => {
        const itemProgress = index < this.activeIndex
          ? 1
          : index === this.activeIndex
            ? progress
            : 0;

        item.style.setProperty('--progress', itemProgress.toFixed(3));
        item.setAttribute('aria-pressed', String(index === this.activeIndex));
      });
    }

    startProgress(index) {
      if (
        index !== this.activeIndex ||
        this.startedAt !== null ||
        !this.shouldRun()
      ) return;

      this.startedAt = performance.now();
      this.requestTick();
    }

    pauseProgress(index) {
      if (index !== this.activeIndex || this.startedAt === null) return;
      this.elapsed += performance.now() - this.startedAt;
      this.startedAt = null;
      this.stopTick();
      this.renderProgress(this.getProgress());
    }

    getProgress(timestamp = performance.now()) {
      const activeVideo = this.getVideo(this.activeIndex);
      if (
        this.getDurationMode(this.activeIndex) === 'full_video' &&
        !this.videoFallbackIndexes.has(this.activeIndex) &&
        activeVideo &&
        Number.isFinite(activeVideo.currentTime) &&
        Number.isFinite(activeVideo.duration) &&
        activeVideo.duration > 0
      ) {
        return clamp(activeVideo.currentTime / activeVideo.duration);
      }

      const currentElapsed = this.startedAt === null
        ? this.elapsed
        : this.elapsed + timestamp - this.startedAt;

      return clamp(currentElapsed / this.getDuration(this.activeIndex));
    }

    requestTick() {
      if (this.frame !== null || this.startedAt === null) return;
      this.frame = window.requestAnimationFrame(this.tick);
    }

    stopTick() {
      if (this.frame !== null) window.cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    tick(timestamp) {
      this.frame = null;
      if (this.startedAt === null) return;

      const progress = this.getProgress(timestamp);
      this.renderProgress(progress);

      if (progress >= 1) {
        this.setActiveModel(this.activeIndex + 1);
        return;
      }

      this.requestTick();
    }

    clearVideoWatchdog() {
      if (this.videoWatchdogTimer !== null) window.clearTimeout(this.videoWatchdogTimer);
      this.videoWatchdogTimer = null;
      this.videoWatchdogIndex = -1;
    }

    armVideoWatchdog(index, type) {
      if (index !== this.activeIndex || !this.shouldRun()) return;
      this.clearVideoWatchdog();
      this.videoWatchdogIndex = index;
      const timeout = type === 'stall' ? this.stallTimeout : this.startupTimeout;
      this.videoWatchdogTimer = window.setTimeout(() => {
        this.videoWatchdogTimer = null;
        this.videoWatchdogIndex = -1;
        this.setVideoState(index, type === 'stall' ? 'buffering-slow' : 'loading-slow');
      }, timeout);
    }

    syncFallbackProgress(index) {
      const video = this.getVideo(index);
      if (
        !video
        || !Number.isFinite(video.currentTime)
        || !Number.isFinite(video.duration)
        || video.duration <= 0
      ) return;

      this.elapsed = this.getConfiguredDuration(index) * clamp(video.currentTime / video.duration);
    }

    useVideoFallback(index, reason = 'unavailable') {
      if (
        index !== this.activeIndex
        || this.reducedMotion.matches
        || this.videoFallbackIndexes.has(index)
      ) return;

      this.clearVideoWatchdog();
      this.pauseProgress(index);
      this.syncFallbackProgress(index);
      this.videoFallbackIndexes.add(index);
      this.mediaItems[index]?.classList.remove('is-video-playing');
      this.detachVideo(index);
      this.setVideoState(index, `fallback-${reason}`);
      this.startProgress(index);
      this.prepareNextVideo(index);
    }

    beginMediaTransition(previousIndex, nextIndex) {
      if (this.transitionCleanupTimer !== null) {
        window.clearTimeout(this.transitionCleanupTimer);
        this.transitionCleanupTimer = null;
      }

      this.mediaItems.forEach((media) => media.classList.remove('is-transitioning'));
      if (previousIndex < 0 || previousIndex === nextIndex) return;

      const previousMedia = this.mediaItems[previousIndex];
      previousMedia?.classList.add('is-transitioning');
      this.transitionCleanupTimer = window.setTimeout(() => {
        previousMedia?.classList.remove('is-transitioning');
        this.transitionCleanupTimer = null;
      }, 1200);
    }

    setActiveModel(index) {
      if (this.total === 0) return;

      const normalizedIndex = (index + this.total) % this.total;
      const previousIndex = this.activeIndex;
      this.stopTick();
      this.clearVideoWatchdog();
      this.activeIndex = normalizedIndex;
      this.elapsed = 0;
      this.startedAt = null;
      this.beginMediaTransition(previousIndex, normalizedIndex);

      this.mediaItems.forEach((media, mediaIndex) => {
        const isActive = mediaIndex === normalizedIndex;
        const video = media.querySelector('[data-hero-model-video]');

        media.classList.toggle('is-active', isActive);
        media.classList.remove('is-video-playing');
        media.setAttribute('aria-hidden', String(!isActive));

        if (!video) return;
        video.pause();
        try {
          if (video.readyState > 0) video.currentTime = 0;
        } catch (error) {
          // Some browsers reject currentTime changes before metadata is available.
        }
      });

      this.pruneVideoWindow(normalizedIndex);
      this.renderProgress(0);

      if (!this.shouldRun()) {
        if (this.reducedMotion.matches) this.releaseAllVideos();
        return;
      }

      const activeMedia = this.mediaItems[normalizedIndex];
      const activeVideo = activeMedia?.querySelector('[data-hero-model-video]');

      if (activeVideo) {
        this.activateVideo(activeVideo, normalizedIndex);
      } else {
        this.startProgress(normalizedIndex);
        this.prepareNextVideo(normalizedIndex);
      }
    }

    activateVideo(video, index = this.activeIndex) {
      if (index !== this.activeIndex || !this.shouldRun()) return;

      this.videoFallbackIndexes.delete(index);
      const attachedVideo = this.attachVideo(index, 'auto');
      if (!attachedVideo) {
        this.useVideoFallback(index, 'missing-source');
        return;
      }

      this.setVideoState(index, attachedVideo.readyState >= 3 ? 'ready' : 'loading');
      this.armVideoWatchdog(index, 'startup');
      const playPromise = attachedVideo.play();
      if (playPromise?.catch) {
        playPromise.catch(() => this.useVideoFallback(index, 'play-rejected'));
      }
    }

    prepareNextVideo(fromIndex) {
      if (this.loadingStrategy !== 'staged') return;
      if (this.reducedMotion.matches || this.total < 2) return;

      for (let offset = 1; offset < this.total; offset += 1) {
        const nextIndex = (fromIndex + offset) % this.total;
        const nextVideo = this.getVideo(nextIndex);
        if (!nextVideo) continue;

        const attachedVideo = this.attachVideo(nextIndex, 'auto');
        if (attachedVideo?.readyState >= 3) this.setVideoState(nextIndex, 'prepared');
        this.pruneVideoWindow(this.activeIndex, nextIndex);
        return nextIndex;
      }

      this.pruneVideoWindow(this.activeIndex);
      return -1;
    }

    onItemClick(event) {
      const item = event.target.closest('[data-model-item]');
      if (!item || !this.root.contains(item)) return;

      this.setActiveModel(Number(item.dataset.modelIndex || 0));
    }

    onPrevious() {
      this.setActiveModel(this.activeIndex - 1);
    }

    onNext() {
      this.setActiveModel(this.activeIndex + 1);
    }

    onMotionPreferenceChange() {
      this.setActiveModel(this.activeIndex < 0 ? 0 : this.activeIndex);
    }

    onBreakpointChange() {
      this.clearVideoWatchdog();
      this.releaseAllVideos();
      this.videoFallbackIndexes.clear();
      this.setActiveModel(this.activeIndex < 0 ? 0 : this.activeIndex);
    }

    pauseActiveModel() {
      this.clearVideoWatchdog();
      this.pauseProgress(this.activeIndex);
      this.getVideo(this.activeIndex)?.pause();
    }

    resumeActiveModel() {
      if (!this.shouldRun() || this.activeIndex < 0) return;

      if (this.videoFallbackIndexes.has(this.activeIndex)) {
        this.startProgress(this.activeIndex);
        return;
      }

      const activeVideo = this.getVideo(this.activeIndex);
      if (activeVideo) {
        this.activateVideo(activeVideo, this.activeIndex);
      } else {
        this.startProgress(this.activeIndex);
        this.prepareNextVideo(this.activeIndex);
      }
    }

    onIntersection(entries) {
      const isVisible = Boolean(entries[0]?.isIntersecting);
      if (isVisible === this.isVisible) return;
      this.isVisible = isVisible;

      if (!isVisible) {
        this.pauseActiveModel();
        return;
      }

      this.resumeActiveModel();
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.pauseActiveModel();
      } else {
        this.resumeActiveModel();
      }
    }

    onPageShow() {
      this.resumeActiveModel();
    }

    destroy() {
      this.stopTick();
      this.clearVideoWatchdog();
      if (this.transitionCleanupTimer !== null) {
        window.clearTimeout(this.transitionCleanupTimer);
        this.transitionCleanupTimer = null;
      }
      this.root.removeEventListener('click', this.onItemClick);
      this.prev?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      this.mobileQuery.removeEventListener?.('change', this.onBreakpointChange);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('pageshow', this.onPageShow);
      this.intersectionObserver?.disconnect();

      this.videoHandlers.forEach(({
        video,
        loadedmetadata,
        canplay,
        playing,
        buffering,
        paused,
        ended,
        error,
        abort,
      }) => {
        video.removeEventListener('loadedmetadata', loadedmetadata);
        video.removeEventListener('canplay', canplay);
        video.removeEventListener('playing', playing);
        video.removeEventListener('waiting', buffering);
        video.removeEventListener('stalled', buffering);
        video.removeEventListener('pause', paused);
        video.removeEventListener('ended', ended);
        video.removeEventListener('error', error);
        video.removeEventListener('abort', abort);
        video.pause();
      });

      this.releaseAllVideos();
      this.managedResetTimers.forEach((timer) => window.clearTimeout(timer));
      this.managedResetTimers.clear();
    }
  }

  class DevelopmentOrbitController {
    constructor(root) {
      this.root = root;
      this.stage = root.querySelector('[data-development-orbit-stage]');
      this.cards = [...root.querySelectorAll('[data-development-orbit-card]')];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.frame = null;
      this.startTime = null;
      this.needsMeasure = true;
      this.isVisible = false;

      this.tick = this.tick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onMotionPreferenceChange = this.onMotionPreferenceChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onDocumentVisibilityChange = this.onDocumentVisibilityChange.bind(this);
    }

    init() {
      if (!this.stage || this.cards.length === 0) return;

      window.addEventListener('resize', this.onResize, { passive: true });
      window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });
      this.reducedMotion.addEventListener?.('change', this.onMotionPreferenceChange);
      document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.stage);
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(this.onVisibilityChange, {
          rootMargin: '200px 0px',
        });
        this.visibilityObserver.observe(this.root);
      } else {
        this.isVisible = true;
        this.start();
      }
    }

    start(resetTimeline = false) {
      this.stop();
      this.needsMeasure = true;

      if (resetTimeline || this.startTime === null) {
        this.startTime = performance.now();
      }

      if (this.reducedMotion.matches) {
        this.measure();
        this.render(0.08);
        return;
      }

      if (!this.isVisible || document.hidden) return;

      this.root.classList.add('is-development-orbit-active');
      this.frame = window.requestAnimationFrame(this.tick);
    }

    stop() {
      if (this.frame !== null) {
        window.cancelAnimationFrame(this.frame);
        this.frame = null;
      }

      this.root.classList.remove('is-development-orbit-active');
    }

    onResize() {
      this.needsMeasure = true;

      if (this.reducedMotion.matches) {
        this.stop();
        this.frame = window.requestAnimationFrame(() => {
          this.frame = null;
          this.measure();
          this.render(0.08);
        });
      }
    }

    onMotionPreferenceChange() {
      this.start(true);
    }

    onVisibilityChange(entries) {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      if (this.isVisible === isVisible) return;

      this.isVisible = isVisible;
      if (isVisible) {
        this.start();
      } else {
        this.stop();
      }
    }

    onDocumentVisibilityChange() {
      if (document.hidden) {
        this.stop();
      } else if (this.isVisible) {
        this.start();
      }
    }

    measure() {
      const bounds = this.stage.getBoundingClientRect();
      const firstCard = this.cards[0];

      this.stageWidth = bounds.width;
      this.stageHeight = bounds.height;
      this.centerX = this.stageWidth * 0.5;
      this.centerY = this.stageHeight * ORBIT_CONFIG.centerY;
      this.radiusX = this.stageWidth * ORBIT_CONFIG.radiusX;
      this.radiusY = this.stageHeight * ORBIT_CONFIG.radiusY;
      this.cardWidth = firstCard.offsetWidth;
      this.cardHeight = firstCard.offsetHeight;
      this.needsMeasure = false;
    }

    tick(timestamp) {
      this.frame = null;
      if (!this.isVisible || document.hidden || this.reducedMotion.matches) return;

      if (this.needsMeasure) {
        this.measure();
      }

      const progress = ((timestamp - this.startTime) % ORBIT_CONFIG.duration) /
        ORBIT_CONFIG.duration;

      this.render(progress);
      this.frame = window.requestAnimationFrame(this.tick);
    }

    render(progress) {
      if (!this.stageWidth || !this.stageHeight) return;

      const cardCount = this.cards.length;

      this.cards.forEach((card, index) => {
        const phase = (progress + index / cardCount) % 1;
        const angle = Math.PI + phase * Math.PI;
        const x = this.centerX + this.radiusX * Math.cos(angle);
        const y = this.centerY + this.radiusY * Math.sin(angle);
        const tangentX = -this.radiusX * Math.sin(angle);
        const tangentY = this.radiusY * Math.cos(angle);
        const tangentAngle = Math.atan2(tangentY, tangentX) * 180 / Math.PI;
        const fadeIn = smoothstep(clamp(phase / ORBIT_CONFIG.edgeFade));
        const fadeOut = smoothstep(clamp((1 - phase) / ORBIT_CONFIG.edgeFade));
        const opacity = Math.min(fadeIn, fadeOut);

        card.style.transform = `translate3d(${(x - this.cardWidth / 2).toFixed(2)}px, ` +
          `${(y - this.cardHeight / 2).toFixed(2)}px, 0) ` +
          `rotate(${(tangentAngle * ORBIT_CONFIG.tangentRotation).toFixed(2)}deg)`;
        card.style.opacity = opacity.toFixed(4);
      });
    }

    destroy() {
      this.stop();
      window.removeEventListener('resize', this.onResize);
      window.visualViewport?.removeEventListener('resize', this.onResize);
      this.reducedMotion.removeEventListener?.('change', this.onMotionPreferenceChange);
      document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
      this.visibilityObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }
  }

  class DevelopmentFaqController {
    constructor(root) {
      this.root = root;
      this.items = [...root.querySelectorAll('[data-development-faq-item]')];
      this.triggers = [...root.querySelectorAll('[data-development-faq-trigger]')];

      this.onClick = this.onClick.bind(this);
    }

    init() {
      this.items.forEach((item) => {
        this.setItemState(item, item.classList.contains('is-open'));
      });
      this.triggers.forEach((trigger) => trigger.addEventListener('click', this.onClick));
    }

    onClick(event) {
      const trigger = event.currentTarget;
      const selectedItem = trigger.closest('[data-development-faq-item]');
      const shouldOpen = !selectedItem.classList.contains('is-open');

      this.items.forEach((item) => {
        this.setItemState(item, item === selectedItem && shouldOpen);
      });
    }

    setItemState(item, isOpen) {
      const trigger = item.querySelector('[data-development-faq-trigger]');
      const panel = item.querySelector('[data-development-faq-panel]');

      item.classList.toggle('is-open', isOpen);
      trigger?.setAttribute('aria-expanded', String(isOpen));
      panel?.setAttribute('aria-hidden', String(!isOpen));
    }

    destroy() {
      this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.onClick));
    }
  }

  class DevelopmentYotpoReviewsController {
    constructor(root) {
      this.root = root;
      this.appKey = root.dataset.yotpoAppKey?.trim();
      this.cards = [...root.querySelectorAll('[data-development-yotpo-review-card]')];
      this.limit = Math.min(
        Math.max(Math.floor(Number(root.dataset.yotpoReviewLimit || this.cards.length)), 1),
        this.cards.length
      );
      this.products = this.getProducts();
      this.viewport = root.querySelector('.development-reviews__viewport');
      this.track = root.querySelector('.development-reviews__track');
      this.loopEnabled = root.dataset.reviewInfiniteLoop !== 'false';
      this.loopSpeed = Math.max(Number(root.dataset.reviewLoopSpeed) || 24, 1);
      this.motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
      this.destroyed = false;
      this.dragState = null;
      this.initialPositionFrame = null;
      this.suppressClick = false;
      this.loopFrame = null;
      this.loopMeasureFrame = null;
      this.loopLastTimestamp = null;
      this.loopWidth = 0;
      this.loopClones = [];
      this.loopPauseReasons = new Set();
      this.onPointerDown = this.handlePointerDown.bind(this);
      this.onPointerMove = this.handlePointerMove.bind(this);
      this.onPointerEnd = this.handlePointerEnd.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onDragStart = (event) => event.preventDefault();
      this.onLoopTick = this.handleLoopTick.bind(this);
      this.onLoopMouseEnter = () => this.pauseLoop('hover');
      this.onLoopMouseLeave = () => this.resumeLoop('hover');
      this.onLoopFocusIn = () => this.pauseLoop('focus');
      this.onLoopFocusOut = this.handleLoopFocusOut.bind(this);
      this.onLoopVisibilityChange = this.handleLoopVisibilityChange.bind(this);
      this.onLoopIntersectionChange = this.handleLoopIntersectionChange.bind(this);
      this.onLoopMotionChange = this.handleLoopMotionChange.bind(this);
      this.onLoopResize = () => this.measureLoop();
    }

    init() {
      this.resetInitialPosition();
      this.initDrag();
      this.initInfiniteLoopControls();

      if (!this.appKey || !this.cards.length) {
        this.setState('fallback');
        this.setupInfiniteLoop();
        return;
      }

      this.setState('loading');
      this.load();
    }

    resetInitialPosition() {
      if (!this.viewport) return;

      this.viewport.scrollLeft = 0;
      this.initialPositionFrame = window.requestAnimationFrame(() => {
        this.initialPositionFrame = null;

        if (!this.destroyed && !this.dragState) {
          this.viewport.scrollLeft = 0;
        }
      });
    }

    initDrag() {
      if (!this.viewport) return;

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerEnd);
      this.viewport.addEventListener('pointercancel', this.onPointerEnd);
      this.viewport.addEventListener('click', this.onClick, true);
      this.viewport.addEventListener('dragstart', this.onDragStart);
    }

    handlePointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button === 0) {
        this.pauseLoop('pointer');
      }

      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      this.dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: this.viewport.scrollLeft,
        hasMoved: false,
      };
      this.viewport.setPointerCapture(event.pointerId);
    }

    handlePointerMove(event) {
      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const distance = event.clientX - this.dragState.startX;
      if (Math.abs(distance) > 5) {
        this.dragState.hasMoved = true;
        this.viewport.classList.add('is-dragging');
      }

      if (!this.dragState.hasMoved) return;

      event.preventDefault();
      this.viewport.scrollLeft = this.dragState.startScrollLeft - distance;
    }

    handlePointerEnd(event) {
      this.resumeLoop('pointer');

      if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

      const hasMoved = this.dragState.hasMoved;
      this.dragState = null;
      this.viewport.classList.remove('is-dragging');

      if (this.viewport.hasPointerCapture(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      if (hasMoved) {
        this.suppressClick = true;
        window.setTimeout(() => {
          this.suppressClick = false;
        }, 0);
      }
    }

    handleClick(event) {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopPropagation();
    }

    async load() {
      try {
        const reviews = await fetchDevelopmentYotpoReviews(this.appKey);
        if (this.destroyed) return;

        const selectedReviews = this.selectReviews(reviews);
        if (selectedReviews.length < this.limit) {
          throw new Error(`Yotpo returned fewer than ${this.limit} usable 5-star reviews.`);
        }

        selectedReviews.forEach((review, index) => this.renderCard(this.cards[index], review, index));
        this.setState('loaded');
        this.setupInfiniteLoop();
      } catch (error) {
        if (this.destroyed) return;

        this.setState('fallback');
        this.setupInfiniteLoop();
        console.warn('Development reviews are using their static fallback.', error);
      }
    }

    selectReviews(reviews) {
      return reviews
        .map((review) => ({
          ...review,
          normalizedContent: this.normalizeText(review?.content),
        }))
        .filter((review) => Number(review?.score) === 5 && review.normalizedContent)
        .sort((reviewA, reviewB) => {
          const verifiedDifference = Number(Boolean(reviewB?.verified_buyer)) - Number(Boolean(reviewA?.verified_buyer));
          if (verifiedDifference) return verifiedDifference;

          const qualityA = reviewA.normalizedContent.length >= 100 && reviewA.normalizedContent.length <= 700;
          const qualityB = reviewB.normalizedContent.length >= 100 && reviewB.normalizedContent.length <= 700;
          if (qualityA !== qualityB) return Number(qualityB) - Number(qualityA);

          return Date.parse(reviewB?.created_at || 0) - Date.parse(reviewA?.created_at || 0);
        })
        .slice(0, this.limit);
    }

    renderCard(card, review, index) {
      const reviewer = card.querySelector('[data-yotpo-reviewer]');
      const content = card.querySelector('[data-yotpo-review-content]');
      const verifiedIcon = card.querySelector('[data-yotpo-verified-icon]');
      const verifiedLabel = card.querySelector('[data-yotpo-verified-label]');
      const isVerified = Boolean(review?.verified_buyer);

      if (reviewer) reviewer.textContent = this.normalizeText(review?.user?.display_name) || 'Groov customer';
      if (content) content.textContent = `“${review.normalizedContent}”`;

      verifiedIcon?.toggleAttribute('hidden', !isVerified);
      verifiedLabel?.toggleAttribute('hidden', !isVerified);
      card.dataset.yotpoReviewId = String(review?.id || '');
      this.renderProduct(card, this.getProductForReview(review, index));
    }

    getProducts() {
      const config = this.root.querySelector('[data-development-review-products]');

      try {
        const products = JSON.parse(config?.textContent || '[]');
        return products.filter((product) => product?.name && product?.image && product?.price);
      } catch (error) {
        console.warn('Development reviews could not read their product catalog.', error);
        return [];
      }
    }

    getProductForReview(review, fallbackIndex) {
      if (!this.products.length) return null;

      const seedSource = String(review?.id || fallbackIndex || 0);
      const seed = [...seedSource].reduce((total, character) => total + character.charCodeAt(0), 0);
      return this.products[seed % this.products.length];
    }

    renderProduct(card, product) {
      if (!product) return;

      const image = card.querySelector('[data-yotpo-product-image]');
      const name = card.querySelector('[data-yotpo-product-name]');
      const price = card.querySelector('[data-yotpo-product-price]');
      const link = card.querySelector('[data-yotpo-product-link]');

      if (image) {
        image.src = this.getStorefrontAssetUrl(product.image);
        image.alt = `${product.name} insole`;
      }
      if (name) name.textContent = product.name;
      if (price) price.textContent = product.price;
      if (link) {
        link.href = product.url || link.href;
        link.setAttribute('aria-label', `Shop ${product.name}`);
      }
    }

    getStorefrontAssetUrl(source) {
      try {
        const url = new URL(source, window.location.href);
        return `${url.pathname}${url.search}${url.hash}`;
      } catch (error) {
        return source;
      }
    }

    normalizeText(value) {
      return String(value || '').replace(/\s+/g, ' ').trim();
    }

    setState(state) {
      this.root.dataset.yotpoState = state;

      if (state === 'loading') {
        this.root.setAttribute('aria-busy', 'true');
      } else {
        this.root.removeAttribute('aria-busy');
      }
    }

    initInfiniteLoopControls() {
      if (!this.loopEnabled || !this.viewport || !this.track) return;

      this.viewport.addEventListener('mouseenter', this.onLoopMouseEnter);
      this.viewport.addEventListener('mouseleave', this.onLoopMouseLeave);
      this.viewport.addEventListener('focusin', this.onLoopFocusIn);
      this.viewport.addEventListener('focusout', this.onLoopFocusOut);
      document.addEventListener('visibilitychange', this.onLoopVisibilityChange);
      window.addEventListener('resize', this.onLoopResize, { passive: true });

      if (this.motionPreference?.addEventListener) {
        this.motionPreference.addEventListener('change', this.onLoopMotionChange);
      } else {
        this.motionPreference?.addListener?.(this.onLoopMotionChange);
      }

      if (document.hidden) {
        this.loopPauseReasons.add('visibility');
      }

      if ('IntersectionObserver' in window) {
        this.loopPauseReasons.add('viewport');
        this.loopVisibilityObserver = new IntersectionObserver(
          this.onLoopIntersectionChange,
          { rootMargin: '200px 0px' }
        );
        this.loopVisibilityObserver.observe(this.root);
      }
    }

    setupInfiniteLoop() {
      if (
        this.destroyed ||
        !this.loopEnabled ||
        !this.viewport ||
        !this.track ||
        this.cards.length < 2 ||
        this.motionPreference?.matches
      ) {
        this.teardownInfiniteLoop();
        return;
      }

      if (!this.loopClones.length) {
        const originalItems = this.cards
          .map((card) => card.closest('.development-reviews__item'))
          .filter(Boolean);
        const beforeFragment = document.createDocumentFragment();
        const afterFragment = document.createDocumentFragment();

        originalItems.forEach((item) => {
          const beforeClone = this.createLoopClone(item, 'before');
          const afterClone = this.createLoopClone(item, 'after');

          beforeFragment.append(beforeClone);
          afterFragment.append(afterClone);
          this.loopClones.push(beforeClone, afterClone);
        });

        this.track.prepend(beforeFragment);
        this.track.append(afterFragment);
        this.viewport.classList.add('is-looping');
      }

      this.measureLoop();
    }

    createLoopClone(item, position) {
      const clone = item.cloneNode(true);

      clone.dataset.developmentReviewLoopClone = position;
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone
        .querySelectorAll('a, button, input, select, textarea, [tabindex]')
        .forEach((element) => element.setAttribute('tabindex', '-1'));

      return clone;
    }

    measureLoop() {
      if (!this.loopClones.length || !this.viewport || !this.track) return;

      if (this.loopMeasureFrame !== null) {
        window.cancelAnimationFrame(this.loopMeasureFrame);
      }

      this.loopMeasureFrame = window.requestAnimationFrame(() => {
        this.loopMeasureFrame = null;
        const firstOriginal = this.cards[0]?.closest('.development-reviews__item');
        const firstAfter = this.track.querySelector('[data-development-review-loop-clone="after"]');
        if (!firstOriginal || !firstAfter) return;

        const previousWidth = this.loopWidth;
        const nextWidth = firstAfter.offsetLeft - firstOriginal.offsetLeft;
        if (nextWidth <= 0) return;

        this.loopWidth = nextWidth;

        if (this.initialPositionFrame !== null) {
          window.cancelAnimationFrame(this.initialPositionFrame);
          this.initialPositionFrame = null;
        }

        if (previousWidth > 0) {
          const previousProgress = this.getLoopProgress(this.viewport.scrollLeft, previousWidth);
          this.viewport.scrollLeft = nextWidth + (previousProgress / previousWidth) * nextWidth;
        } else {
          this.viewport.scrollLeft = nextWidth;
        }

        this.startLoop();
      });
    }

    getLoopProgress(scrollLeft, width = this.loopWidth) {
      if (!width) return 0;
      return ((scrollLeft - width) % width + width) % width;
    }

    normalizeLoopPosition() {
      if (!this.loopWidth || !this.viewport) return;

      const progress = this.getLoopProgress(this.viewport.scrollLeft);
      this.viewport.scrollLeft = this.loopWidth + progress;
    }

    startLoop() {
      if (
        this.loopFrame !== null ||
        !this.loopWidth ||
        this.loopPauseReasons.size ||
        this.motionPreference?.matches ||
        document.hidden
      ) {
        return;
      }

      this.loopLastTimestamp = null;
      this.loopFrame = window.requestAnimationFrame(this.onLoopTick);
    }

    stopLoop() {
      if (this.loopFrame !== null) {
        window.cancelAnimationFrame(this.loopFrame);
        this.loopFrame = null;
      }

      this.loopLastTimestamp = null;
    }

    handleLoopTick(timestamp) {
      this.loopFrame = null;
      if (this.destroyed || this.loopPauseReasons.size || !this.loopWidth || !this.viewport) return;

      if (this.loopLastTimestamp !== null) {
        const elapsed = Math.min(timestamp - this.loopLastTimestamp, 64);
        this.viewport.scrollLeft += (this.loopSpeed * elapsed) / 1000;

        if (
          this.viewport.scrollLeft < this.loopWidth ||
          this.viewport.scrollLeft >= this.loopWidth * 2
        ) {
          this.normalizeLoopPosition();
        }
      }

      this.loopLastTimestamp = timestamp;
      this.loopFrame = window.requestAnimationFrame(this.onLoopTick);
    }

    pauseLoop(reason) {
      if (!this.loopEnabled) return;

      this.loopPauseReasons.add(reason);
      this.stopLoop();
    }

    resumeLoop(reason) {
      if (!this.loopEnabled) return;

      this.loopPauseReasons.delete(reason);
      if (!this.loopPauseReasons.size) {
        this.normalizeLoopPosition();
        this.startLoop();
      }
    }

    handleLoopFocusOut(event) {
      if (!this.viewport?.contains(event.relatedTarget)) {
        this.resumeLoop('focus');
      }
    }

    handleLoopVisibilityChange() {
      if (document.hidden) {
        this.pauseLoop('visibility');
      } else {
        this.resumeLoop('visibility');
      }
    }

    handleLoopIntersectionChange(entries) {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      this.root.classList.toggle('is-development-reviews-active', isVisible);

      if (isVisible) {
        this.resumeLoop('viewport');
      } else {
        this.pauseLoop('viewport');
      }
    }

    handleLoopMotionChange() {
      if (this.motionPreference?.matches) {
        this.teardownInfiniteLoop();
      } else {
        this.setupInfiniteLoop();
      }
    }

    teardownInfiniteLoop() {
      this.stopLoop();

      if (this.loopMeasureFrame !== null) {
        window.cancelAnimationFrame(this.loopMeasureFrame);
        this.loopMeasureFrame = null;
      }

      const progress = this.getLoopProgress(this.viewport?.scrollLeft || 0);
      this.loopClones.forEach((clone) => clone.remove());
      this.loopClones = [];
      this.loopWidth = 0;
      this.viewport?.classList.remove('is-looping');

      if (this.viewport) {
        this.viewport.scrollLeft = progress;
      }
    }

    destroy() {
      this.destroyed = true;
      this.teardownInfiniteLoop();

      if (this.initialPositionFrame !== null) {
        window.cancelAnimationFrame(this.initialPositionFrame);
        this.initialPositionFrame = null;
      }

      this.viewport?.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerEnd);
      this.viewport?.removeEventListener('pointercancel', this.onPointerEnd);
      this.viewport?.removeEventListener('click', this.onClick, true);
      this.viewport?.removeEventListener('dragstart', this.onDragStart);
      this.viewport?.removeEventListener('mouseenter', this.onLoopMouseEnter);
      this.viewport?.removeEventListener('mouseleave', this.onLoopMouseLeave);
      this.viewport?.removeEventListener('focusin', this.onLoopFocusIn);
      this.viewport?.removeEventListener('focusout', this.onLoopFocusOut);
      document.removeEventListener('visibilitychange', this.onLoopVisibilityChange);
      window.removeEventListener('resize', this.onLoopResize);
      this.loopVisibilityObserver?.disconnect();
      this.root.classList.remove('is-development-reviews-active');

      if (this.motionPreference?.removeEventListener) {
        this.motionPreference.removeEventListener('change', this.onLoopMotionChange);
      } else {
        this.motionPreference?.removeListener?.(this.onLoopMotionChange);
      }
    }
  }

  class DevelopmentBenefitsController {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-development-benefits-viewport]');
      this.items = [...root.querySelectorAll('[data-development-benefits-item]')];
      this.dots = [...root.querySelectorAll('[data-development-benefits-dot]')];
      this.scrollFrame = null;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
    }

    init() {
      if (!this.viewport || !this.items.length) return;

      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      this.updateActiveDot(0);
    }

    onScroll() {
      if (this.scrollFrame !== null) return;

      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.syncToScrollPosition();
      });
    }

    onResize() {
      this.onScroll();
    }

    onDotClick(event) {
      const index = Number(event.currentTarget.dataset.developmentBenefitsDot || 0);
      const item = this.items[index];

      if (!item) return;

      this.updateActiveDot(index);
      this.viewport.scrollTo({
        left: this.getItemScrollLeft(item),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    getItemScrollLeft(item) {
      return item.offsetLeft - (this.items[0]?.offsetLeft || 0);
    }

    syncToScrollPosition() {
      const currentLeft = this.viewport.scrollLeft;
      let activeIndex = 0;
      let closestDistance = Infinity;

      this.items.forEach((item, index) => {
        const distance = Math.abs(this.getItemScrollLeft(item) - currentLeft);

        if (distance < closestDistance) {
          closestDistance = distance;
          activeIndex = index;
        }
      });

      this.updateActiveDot(activeIndex);
    }

    updateActiveDot(activeIndex) {
      this.dots.forEach((dot, index) => {
        const isActive = index === activeIndex;

        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-pressed', String(isActive));
      });
    }

    destroy() {
      this.viewport?.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));

      if (this.scrollFrame !== null) {
        window.cancelAnimationFrame(this.scrollFrame);
      }
    }
  }

  class DevelopmentExpertVideoController {
    constructor(root) {
      this.root = root;
      this.trigger = root.querySelector('[data-development-expert-video-trigger]');
      this.dialog = root.querySelector('[data-development-expert-video-dialog]');
      this.closeButton = root.querySelector('[data-development-expert-video-close]');
      this.player = root.querySelector('[data-development-expert-video-player]');
      this.source = root.querySelector('[data-development-expert-video-source]');

      this.onOpen = this.onOpen.bind(this);
      this.onClose = this.onClose.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onDialogClosed = this.onDialogClosed.bind(this);
    }

    init() {
      if (!this.trigger || !this.dialog || !this.player) return;

      this.trigger.addEventListener('click', this.onOpen);
      this.closeButton?.addEventListener('click', this.onClose);
      this.dialog.addEventListener('click', this.onDialogClick);
      this.dialog.addEventListener('close', this.onDialogClosed);
    }

    onOpen() {
      this.ensurePlayerSource();

      if (!this.dialog.open && typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else if (!this.dialog.open) {
        this.dialog.setAttribute('open', '');
      }

      const playPromise = this.player.play();
      playPromise?.catch(() => {});
    }

    ensurePlayerSource() {
      if (!this.player || !this.source || this.source.hasAttribute('src')) return;

      const source = this.source.dataset.src?.trim();
      if (!source) return;

      this.source.setAttribute('src', source);
      this.player.load();
    }

    onClose() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.resetPlayer();
      }
    }

    onDialogClick(event) {
      if (event.target === this.dialog) this.onClose();
    }

    onDialogClosed() {
      this.resetPlayer();
      this.trigger?.focus({ preventScroll: true });
    }

    resetPlayer() {
      this.player?.pause();

      try {
        this.player.currentTime = 0;
      } catch (error) {
        // The player may not have loaded enough metadata to seek yet.
      }
    }

    destroy() {
      if (this.dialog?.open) {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      }

      this.resetPlayer();
      this.trigger?.removeEventListener('click', this.onOpen);
      this.closeButton?.removeEventListener('click', this.onClose);
      this.dialog?.removeEventListener('click', this.onDialogClick);
      this.dialog?.removeEventListener('close', this.onDialogClosed);
    }
  }

  const initDevelopmentSections = (scope = document) => {
    scope.querySelectorAll('[data-development]').forEach((root) => {
      if (root.developmentScrollController) return;

      const frameCoordinator = new DevelopmentFrameCoordinator();
      const controller = new DevelopmentScrollController(root, frameCoordinator);
      root.developmentFrameCoordinator = frameCoordinator;
      root.developmentScrollController = controller;
      controller.init();
    });
  };

  const initDevelopmentScrollSmoothers = (scope = document) => {
    scope.querySelectorAll('[data-development-scroll-smoother-enabled]').forEach((root) => {
      if (root.developmentScrollSmootherController) return;

      const controller = new DevelopmentScrollSmootherController(root);
      root.developmentScrollSmootherController = controller;
      controller.init();
    });
  };

  const initDevelopmentHeroCarousels = (scope = document) => {
    scope.querySelectorAll('[data-hero-model-carousel]').forEach((root) => {
      if (root.developmentHeroModelCarousel) return;

      const controller = new DevelopmentHeroModelCarousel(root);
      root.developmentHeroModelCarousel = controller;
      controller.init();
    });
  };

  const initDevelopmentOrbitSections = (scope = document) => {
    scope.querySelectorAll('[data-development-orbit]').forEach((root) => {
      if (root.developmentOrbitController) return;

      const controller = new DevelopmentOrbitController(root);
      root.developmentOrbitController = controller;
      controller.init();
    });
  };

  const initDevelopmentGlassCards = (scope = document) => {
    scope.querySelectorAll('[data-development-choice-card][data-development-choice-appearance="glass"]')
      .forEach((root) => {
        if (root.developmentGlassCardController) return;

        const controller = new DevelopmentGlassCardController(root);
        root.developmentGlassCardController = controller;
        controller.init();
      });
  };

  const initDevelopmentChoiceButtonMotions = (scope = document) => {
    scope.querySelectorAll('[data-development-choice-button-motion]').forEach((root) => {
      if (root.developmentChoiceButtonMotionController) return;

      const controller = new DevelopmentChoiceButtonMotionController(root);
      root.developmentChoiceButtonMotionController = controller;
      controller.init();
    });
  };

  const initDevelopmentChoiceCardMotions = (scope = document) => {
    scope.querySelectorAll('[data-development-choice-card-motion]').forEach((root) => {
      if (root.developmentChoiceCardMotionController) return;

      const controller = new DevelopmentChoiceCardMotionController(root);
      root.developmentChoiceCardMotionController = controller;
      controller.init();
    });
  };

  const initDevelopmentLiquidGlassV2 = (scope = document) => {
    scope.querySelectorAll('[data-development-liquidglass-v2-root]').forEach((root) => {
      if (root.developmentLiquidGlassV2Controller) return;

      const controller = new DevelopmentLiquidGlassV2Controller(root);
      root.developmentLiquidGlassV2Controller = controller;
      controller.init();
    });
  };

  const initDevelopmentInsoleMaps = (scope = document) => {
    scope.querySelectorAll('[data-development-insole-map]').forEach((root) => {
      if (root.developmentInsoleMapController) return;

      const controller = new DevelopmentInsoleMapController(root);
      root.developmentInsoleMapController = controller;
      controller.init();
    });
  };

  const initDevelopmentSteps = (scope = document) => {
    scope.querySelectorAll('[data-development-steps]').forEach((root) => {
      if (root.developmentStepsController) return;

      const controller = new DevelopmentStepsController(root);
      root.developmentStepsController = controller;
      controller.init();
    });
  };

  const initDevelopmentModels = (scope = document) => {
    scope.querySelectorAll('[data-development-models]').forEach((root) => {
      if (root.developmentModelsController) return;

      const controller = new DevelopmentModelsController(root);
      root.developmentModelsController = controller;
      controller.init();
    });
  };

  const initDevelopmentFaqs = (scope = document) => {
    scope.querySelectorAll('[data-development-faq]').forEach((root) => {
      if (root.developmentFaqController) return;

      const controller = new DevelopmentFaqController(root);
      root.developmentFaqController = controller;
      controller.init();
    });
  };

  const initDevelopmentYotpoReviews = (scope = document) => {
    scope.querySelectorAll('[data-development-yotpo-reviews]').forEach((root) => {
      if (root.developmentYotpoReviewsController) return;

      const controller = new DevelopmentYotpoReviewsController(root);
      root.developmentYotpoReviewsController = controller;
      controller.init();
    });
  };

  const initDevelopmentBenefits = (scope = document) => {
    scope.querySelectorAll('[data-development-benefits]').forEach((root) => {
      if (root.developmentBenefitsController) return;

      const controller = new DevelopmentBenefitsController(root);
      root.developmentBenefitsController = controller;
      controller.init();
    });
  };

  const initDevelopmentExpertVideos = (scope = document) => {
    scope.querySelectorAll('[data-development-expert-video]').forEach((root) => {
      if (root.developmentExpertVideoController) return;

      const controller = new DevelopmentExpertVideoController(root);
      root.developmentExpertVideoController = controller;
      controller.init();
    });
  };

  const initDevelopmentReveals = (scope = document) => {
    scope.querySelectorAll('[data-development-reveal]').forEach((root) => {
      if (root.developmentRevealController) return;

      const controller = new DevelopmentRevealController(root);
      root.developmentRevealController = controller;
      controller.init();
    });
  };

  const hydrateDevelopmentModelSources = (scope = document) => {
    scope.querySelectorAll('[data-development-models]').forEach((root) => {
      if (root.dataset.developmentModelsHydrated === 'true') return;

      const sources = [...root.querySelectorAll('template[data-development-model-source]')];
      const tabsTarget = root.querySelector('[data-development-model-tabs]');
      const panelsTarget = root.querySelector('[data-development-model-panels]');
      const visualsTarget = root.querySelector('[data-development-model-visuals]');
      if (!sources.length || !tabsTarget || !panelsTarget || !visualsTarget) return;

      sources.forEach((source) => {
        const tab = source.content.querySelector('[data-development-model-source-tab]');
        const panel = source.content.querySelector('[data-development-model-source-panel]');
        const visual = source.content.querySelector('[data-development-model-source-visual]');

        if (tab) tabsTarget.append(tab.cloneNode(true));
        if (panel) panelsTarget.append(panel.cloneNode(true));
        if (visual) visualsTarget.append(visual.cloneNode(true));
        source.remove();
      });

      root.dataset.developmentModelsHydrated = 'true';
    });
  };

  const hydrateDevelopmentExpertGroups = (scope = document) => {
    scope.querySelectorAll('[data-development-experts-cards]').forEach((cards) => {
      const count = cards.querySelectorAll(':scope > .development-expert-card').length;
      cards.dataset.expertCardCount = String(count);
    });

    scope.querySelectorAll('[data-development-logo-track]').forEach((track) => {
      if (track.dataset.developmentLogoHydrated === 'true') return;

      const source = track.querySelector('[data-development-logo-source]');
      if (!source || source.children.length === 0) return;

      const duplicate = source.cloneNode(true);
      duplicate.removeAttribute('data-development-logo-source');
      duplicate.setAttribute('aria-hidden', 'true');
      duplicate.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      duplicate.querySelectorAll('img').forEach((image) => {
        image.alt = '';
      });

      track.append(duplicate);
      track.dataset.developmentLogoHydrated = 'true';
    });
  };

  const initAllDevelopmentSections = (scope = document) => {
    hydrateDevelopmentModelSources(scope);
    hydrateDevelopmentExpertGroups(scope);
    initDevelopmentScrollSmoothers(scope);
    initDevelopmentHeroCarousels(scope);
    initDevelopmentSections(scope);
    initDevelopmentOrbitSections(scope);
    initDevelopmentChoiceCardMotions(scope);
    initDevelopmentChoiceButtonMotions(scope);
    initDevelopmentGlassCards(scope);
    initDevelopmentLiquidGlassV2(scope);
    initDevelopmentInsoleMaps(scope);
    initDevelopmentSteps(scope);
    initDevelopmentModels(scope);
    initDevelopmentYotpoReviews(scope);
    initDevelopmentFaqs(scope);
    initDevelopmentBenefits(scope);
    initDevelopmentExpertVideos(scope);
    initDevelopmentReveals(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllDevelopmentSections(), { once: true });
  } else {
    initAllDevelopmentSections();
  }

  document.addEventListener('shopify:section:load', (event) => initAllDevelopmentSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-development]');
    const orbitRoot = event.target.querySelector('[data-development-orbit]');

    root?.developmentScrollSmootherController?.destroy();
    event.target.querySelectorAll('[data-hero-model-carousel]').forEach((carouselRoot) => {
      carouselRoot.developmentHeroModelCarousel?.destroy();
    });
    root?.developmentScrollController?.destroy();
    orbitRoot?.developmentOrbitController?.destroy();
    event.target.querySelectorAll('[data-development-choice-card]').forEach((card) => {
      card.developmentGlassCardController?.destroy();
      card.developmentChoiceCardMotionController?.destroy();
    });
    event.target.querySelectorAll('[data-development-choice-button-motion]').forEach((motionRoot) => {
      motionRoot.developmentChoiceButtonMotionController?.destroy();
    });
    event.target.querySelectorAll('[data-development-liquidglass-v2-root]').forEach((glassRoot) => {
      glassRoot.developmentLiquidGlassV2Controller?.destroy();
    });
    event.target.querySelectorAll('[data-development-insole-map]').forEach((mapRoot) => {
      mapRoot.developmentInsoleMapController?.destroy();
    });
    event.target.querySelectorAll('[data-development-steps]').forEach((stepsRoot) => {
      stepsRoot.developmentStepsController?.destroy();
    });
    event.target.querySelectorAll('[data-development-models]').forEach((modelsRoot) => {
      modelsRoot.developmentModelsController?.destroy();
    });
    event.target.querySelectorAll('[data-development-faq]').forEach((faqRoot) => {
      faqRoot.developmentFaqController?.destroy();
    });
    event.target.querySelectorAll('[data-development-yotpo-reviews]').forEach((reviewsRoot) => {
      reviewsRoot.developmentYotpoReviewsController?.destroy();
    });
    event.target.querySelectorAll('[data-development-benefits]').forEach((benefitsRoot) => {
      benefitsRoot.developmentBenefitsController?.destroy();
    });
    event.target.querySelectorAll('[data-development-expert-video]').forEach((videoRoot) => {
      videoRoot.developmentExpertVideoController?.destroy();
    });
    event.target.querySelectorAll('[data-development-reveal]').forEach((revealRoot) => {
      revealRoot.developmentRevealController?.destroy();
    });
    root?.developmentFrameCoordinator?.destroy();
  });
})();
